import React from "react";
import {
    ArrowLeft,
    BookOpen,
    CalendarDays,
    Clock,
    GraduationCap,
    Mail,
    Menu,
    X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { groupLessonCalendarEvents } from "../lib/lessonStage";
import LessonStagePanel, { LessonCalendarDetails } from "../components/LessonStagePanel";
import ClassroomNotesPanel from "../components/classroom/workspace/ClassroomNotesPanel";
import useClassroomDashboard from "../lib/useClassroomDashboard";
import { CLASSROOM_QUEST_TYPES, questsForClass } from "../lib/classroomSchedule";
import { classroomEventOnDate } from "../lib/dashboardGadgets";
import { api } from "../lib/api";
import HomeroomMessages from "../components/HomeroomMessages";
import { classroomCalendarStyle } from "../lib/classroomCalendarStyle";
import { shadowCalendarShell, shadowCalendarToolbar, shadowCalendarGrid, shadowCalendarCell, shadowCalendarCellMuted } from "../shadowGrove/ui/shadowStyles";

const API_BASE_URL = `${
    process.env.REACT_APP_BACKEND_URL ||
    "http://127.0.0.1:8001"
}/api`;

const purpleActionBtn = `
    inline-flex items-center justify-center gap-2
    rounded-2xl
    border border-[#ffd166]/45
    bg-[#2b1548]/70
    px-4 py-2
    text-sm font-semibold text-[#fff3c4]
    shadow-[0_0_18px_rgba(168,85,247,0.22)]
    transition-all duration-200
    hover:border-[#ffd166]
    hover:bg-[#3b1d62]
    hover:text-[#ffd166]
`;

const compactClassroomButtonClass = (isActive) => `
    inline-flex items-center justify-center gap-2
    rounded-2xl
    border
    px-4 py-2.5
    text-sm font-semibold
    transition-all duration-200
    ${
        isActive
            ? `
                border-[#ffd166]
                bg-[#7c3aed]/25
                text-[#ffd166]
                shadow-[0_0_20px_rgba(168,85,247,0.38)]
            `
            : `
                border-[#ffd166]/25
                bg-black/70
                text-[#fff3c4]
                hover:border-[#ffd166]/65
                hover:bg-[#7c3aed]/10
            `
    }
`;

const safelyReadStorage = (key, fallback = []) => {
    try {
        const storedValue = localStorage.getItem(key);

        if (!storedValue) {
            return fallback;
        }

        return JSON.parse(storedValue);
    } catch (error) {
        console.error(`Unable to read ${key}:`, error);
        return fallback;
    }
};

const formatLocalDate = (date) => {
    const year = date.getFullYear();

    const month = String(
        date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
        date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getEventDate = (event) =>
    event?.date_start ||
    event?.date ||
    event?.due_date ||
    event?.scheduled_date ||
    "";

const getEventStartTime = (event) =>
    event?.scheduled_start_time ||
    event?.start_time ||
    event?.due_time ||
    "";

const getEventEndTime = (event) =>
    event?.scheduled_end_time ||
    event?.end_time ||
    "";

const isSessionEvent = (event) =>
    Boolean(
        event?.is_calendar_session ||
            event?.is_timed_session ||
            event?.created_from ===
                "class_timer_lesson_flow" ||
            event?.session_flow_id
    );

const timeToMinutes = (time) => {
    if (
        !time ||
        !String(time).includes(":")
    ) {
        return null;
    }

    const [hours, minutes] = String(time)
        .split(":")
        .map(Number);

    if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes)
    ) {
        return null;
    }

    return hours * 60 + minutes;
};

const formatCalendarTime = (time) => {
    if (
        !time ||
        !String(time).includes(":")
    ) {
        return "";
    }

    const [hours, minutes] = String(time)
        .split(":")
        .map(Number);

    if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes)
    ) {
        return String(time);
    }

    const suffix =
        hours >= 12 ? "PM" : "AM";

    const displayHour =
        hours % 12 === 0
            ? 12
            : hours % 12;

    return `${displayHour}:${String(
        minutes
    ).padStart(2, "0")} ${suffix}`;
};

const formatTimer = (secondsValue) => {
    const safeSeconds = Math.max(
        0,
        Math.floor(
            Number(secondsValue) || 0
        )
    );

    const hours = Math.floor(
        safeSeconds / 3600
    );

    const minutes = Math.floor(
        (safeSeconds % 3600) / 60
    );

    const seconds =
        safeSeconds % 60;

    if (hours > 0) {
        return `${String(hours).padStart(
            2,
            "0"
        )}:${String(minutes).padStart(
            2,
            "0"
        )}:${String(seconds).padStart(
            2,
            "0"
        )}`;
    }

    return `${String(minutes).padStart(
        2,
        "0"
    )}:${String(seconds).padStart(
        2,
        "0"
    )}`;
};

const getElapsedSince = (timestamp) => {
    if (!timestamp) return 0;

    const timestampMilliseconds =
        new Date(timestamp).getTime();

    if (
        Number.isNaN(
            timestampMilliseconds
        )
    ) {
        return 0;
    }

    return Math.max(
        0,
        Math.floor(
            (Date.now() -
                timestampMilliseconds) /
                1000
        )
    );
};

const homeNavigationItems = [
    {
        id: "lessonStage",
        label: "Lesson Stage",
        icon: Clock,
    },
    {
        id: "classes",
        label: "Classes",
        icon: GraduationCap,
    },
    {
        id: "calendar",
        label: "School Calendar",
        icon: CalendarDays,
    },
    {
        id: "messages",
        label: "Messages",
        icon: Mail,
    },
];

export default function ChildClassroom() {
    const navigate = useNavigate();

    const {
        childId,
        subjectId,
    } = useParams();

    const [activePanel, setActivePanel] =
        React.useState(() => new URLSearchParams(window.location.search).get("panel") === "lessonStage" ? "lessonStage" : "bulletin");
    const [
        selectedChildQuestItem,
        setSelectedChildQuestItem,
    ] = React.useState(null);
    const [isClassroomNotesOpen, setIsClassroomNotesOpen] =
    React.useState(true);

    const [layout, setLayout] =
        React.useState({
            workspaceWidth: 520,
        });
    const startClassroomWorkspaceResize = (event) => {
        event.preventDefault();

        const handlePointerMove = (moveEvent) => {
            const nextWidth =
                window.innerWidth - moveEvent.clientX;

            const clampedWidth = Math.min(
                window.innerWidth * 0.72,
                Math.max(260, nextWidth)
            );

            setLayout((prev) => ({
                ...prev,
                workspaceWidth: clampedWidth,
            }));
        };

        const handlePointerUp = () => {
            document.removeEventListener(
                "pointermove",
                handlePointerMove
            );

            document.removeEventListener(
                "pointerup",
                handlePointerUp
            );

            document.body.style.removeProperty(
                "user-select"
            );

            document.body.style.removeProperty(
                "cursor"
            );
        };

        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";

        document.addEventListener(
            "pointermove",
            handlePointerMove
        );

        document.addEventListener(
            "pointerup",
            handlePointerUp
        );
    };
    React.useEffect(() => {if (new URLSearchParams(window.location.search).get("panel") === "lessonStage") setActivePanel("lessonStage");}, [childId, subjectId]);
    const [calendarView, setCalendarView] =
        React.useState("week");

    const [calendarSelectedDate, setCalendarSelectedDate] =
        React.useState(() =>
            formatLocalDate(new Date())
        );

    const [calendarMonthDate, setCalendarMonthDate] =
        React.useState(() =>
            new Date(
                new Date().getFullYear(),
                new Date().getMonth(),
                1
            )
        );
    const [
        classroomMenuOpen,
        setClassroomMenuOpen,
    ] = React.useState(false);

    const [children, setChildren] =
    React.useState(() =>
        safelyReadStorage(
            "shadowGroveChildren",
            []
        )
    );

    const [
        authenticatedChild,
        setAuthenticatedChild,
    ] = React.useState(null);

    const [classroomLoading, setClassroomLoading] = React.useState(true);
    const [classroomError, setClassroomError] = React.useState("");

    const [subjects, setSubjects] =
        React.useState(() =>
            safelyReadStorage(
                "shadowGroveSubjects",
                []
            )
        );

    const [events, setEvents] =
        React.useState(() =>
            safelyReadStorage(
                "shadowGroveEvents",
                []
            )
        );
    React.useEffect(() => {
    const refreshClassroomEvents = () => {
        setEvents(
            safelyReadStorage(
                "shadowGroveEvents",
                []
            )
        );
    };

    const handleStorageChange = (event) => {
        if (
            event.key ===
            "shadowGroveEvents"
        ) {
            refreshClassroomEvents();
        }
    };

    const handleWindowFocus = () => {
        refreshClassroomEvents();
    };

    refreshClassroomEvents();

    window.addEventListener(
        "storage",
        handleStorageChange
    );

    window.addEventListener(
        "focus",
        handleWindowFocus
    );

    return () => {
        window.removeEventListener(
            "storage",
            handleStorageChange
        );

        window.removeEventListener(
            "focus",
            handleWindowFocus
        );
    };
}, []);
    const [quests, setQuests] =
    React.useState([]);

    const [classArchivum, setClassArchivum] =
        React.useState({
            folders: [],
        });

    const [
        openArchivumFolderId,
        setOpenArchivumFolderId,
    ] = React.useState(null);

    const [
        liveSessionStates,
        setLiveSessionStates,
    ] = React.useState({});

    const [
        lessonFlowModalSession,
        setLessonFlowModalSession,
    ] = React.useState(null);

    const [, setClockTick] =
        React.useState(0);

    React.useEffect(() => {
        const clockInterval =
            window.setInterval(() => {
                setClockTick(
                    (currentTick) =>
                        currentTick + 1
                );
            }, 1000);

        return () =>
            window.clearInterval(
                clockInterval
            );
    }, []);

React.useEffect(() => {
    let cancelled = false;
    setClassroomLoading(true);
    setClassroomError("");

    const loadAuthenticatedChild =
        async () => {
            try {
                const [
                    childResponse,
                    subjectsResponse,
                ] = await Promise.all([
                    api.get("/auth/child/me"),
                    api.get("/auth/child/subjects"),
                ]);

                if (cancelled) {
                    return;
                }

                const currentChild =
                    childResponse.data;

                const currentSubjects =
                    Array.isArray(
                        subjectsResponse.data
                    )
                        ? subjectsResponse.data
                        : [];

                if (
                    !currentChild?.child_id
                ) {
                    return;
                }

                setAuthenticatedChild(
                    currentChild
                );

                setSubjects(
                    currentSubjects
                );
            } catch (error) {
                if (cancelled) {
                    return;
                }

                setClassroomError(error?.response?.data?.detail || "Unable to load your classroom. Please sign in again.");
            } finally {
                if (!cancelled) setClassroomLoading(false);
            }
        };

    void loadAuthenticatedChild();

    return () => {
        cancelled = true;
    };
}, []);
React.useEffect(() => {
    if (!childId) {
        setQuests([]);
        return;
    }

    let cancelled = false;

    const loadChildQuests = async () => {
        try {
            const response = await api.get(
                `/children/${childId}/quests`
            );

            if (cancelled) {
                return;
            }

            setQuests(
                Array.isArray(response.data)
                    ? response.data
                    : []
            );
        } catch (error) {
            if (cancelled) {
                return;
            }

            console.error(
                "Unable to load child quests:",
                error
            );

            setQuests([]);
        }
    };

    void loadChildQuests();

    return () => {
        cancelled = true;
    };
}, [childId]);

React.useEffect(() => {
    if (
        !childId ||
        !subjectId
    ) {
        setClassArchivum({
            folders: [],
        });
        return;
    }

    let cancelled = false;

    const loadClassArchivum =
        async () => {
            try {
                const response =
                    await api.get(
                        `/children/${childId}/classes/${subjectId}/archivum`
                    );

                if (cancelled) {
                    return;
                }

                setClassArchivum({
                    ...response.data,
                    folders: Array.isArray(
                        response.data?.folders
                    )
                        ? response.data.folders
                        : [],
                });
            } catch (error) {
                if (cancelled) {
                    return;
                }

                console.error(
                    "Unable to load child Archivum:",
                    error
                );

                setClassArchivum({
                    folders: [],
                });
            }
        };

    void loadClassArchivum();

    return () => {
        cancelled = true;
    };
}, [
    childId,
    subjectId,
]);

const selectedArchivumFolder =
    React.useMemo(
        () =>
            (
                classArchivum.folders ||
                []
            ).find(
                (folder) =>
                    String(
                        folder.id ??
                            folder.folder_id
                    ) ===
                    String(
                        openArchivumFolderId
                    )
            ) || null,
        [
            classArchivum.folders,
            openArchivumFolderId,
        ]
    );

const visibleArchivumFolders =
    React.useMemo(
        () =>
            (
                classArchivum.folders ||
                []
            )
                .filter(
                    (folder) =>
                        String(
                            folder.parent_folder_id ??
                                ""
                        ) ===
                        String(
                            openArchivumFolderId ??
                                ""
                        )
                )
                .sort(
                    (
                        firstFolder,
                        secondFolder
                    ) =>
                        Number(
                            firstFolder.sort_order ||
                                0
                        ) -
                        Number(
                            secondFolder.sort_order ||
                                0
                        )
                ),
        [
            classArchivum.folders,
            openArchivumFolderId,
        ]
    );

React.useEffect(() => {
    const refreshClassroomData =
            () => {
                setChildren(
                    safelyReadStorage(
                        "shadowGroveChildren",
                        []
                    )
                );

                void api.get("/auth/child/subjects").then((response) => {
                    if (Array.isArray(response.data)) setSubjects(response.data);
                }).catch(() => {});

                setEvents(
                    safelyReadStorage(
                        "shadowGroveEvents",
                        []
                    )
                );
            };

        window.addEventListener(
            "storage",
            refreshClassroomData
        );

        window.addEventListener(
            "shadowGroveChildrenUpdated",
            refreshClassroomData
        );

        window.addEventListener(
            "shadowGroveSubjectsUpdated",
            refreshClassroomData
        );

        window.addEventListener(
            "shadowGroveEventsUpdated",
            refreshClassroomData
        );

        return () => {
            window.removeEventListener(
                "storage",
                refreshClassroomData
            );

            window.removeEventListener(
                "shadowGroveChildrenUpdated",
                refreshClassroomData
            );

            window.removeEventListener(
                "shadowGroveSubjectsUpdated",
                refreshClassroomData
            );

            window.removeEventListener(
                "shadowGroveEventsUpdated",
                refreshClassroomData
            );
        };
    }, []);

    const child = React.useMemo(() => {
    const authenticatedChildId =
        authenticatedChild?.child_id ??
        authenticatedChild?.id;

    if (
        authenticatedChildId &&
        String(authenticatedChildId) ===
            String(childId)
    ) {
        return authenticatedChild;
    }

    return (
        children.find(
            (currentChild) =>
                String(
                    currentChild.child_id ??
                        currentChild.id
                ) === String(childId)
        ) || null
    );
}, [
    authenticatedChild,
    children,
    childId,
]);

    const childSubjects =
        React.useMemo(
            () =>
                subjects.filter(
                    (subject) =>
                        String(
                            subject.child_id
                        ) ===
                        String(childId)
                ),
            [subjects, childId]
        );

    const selectedSubject =
        React.useMemo(
            () =>
                childSubjects.find(
                    (subject) =>
                        String(
                            subject.subject_id ??
                                subject.id
                        ) ===
                        String(subjectId)
                ) || null,
            [
                childSubjects,
                subjectId,
            ]
        );

    const subjectsById =
        React.useMemo(() => {
            const subjectMap = {};

            childSubjects.forEach(
                (subject) => {
                    const currentSubjectId =
                        subject.subject_id ??
                        subject.id;

                    if (!currentSubjectId) {
                        return;
                    }

                    subjectMap[
                        String(currentSubjectId)
                    ] = subject;
                }
            );

            return subjectMap;
        }, [childSubjects]);

    React.useEffect(() => {
        if (
            !childId ||
            childSubjects.length === 0
        ) {
            setLiveSessionStates({});
            return undefined;
        }

        let cancelled = false;

        const loadLiveSessions =
            async () => {
                const sessionResults =
                    await Promise.all(
                        childSubjects.map(
                            async (
                                subject
                            ) => {
                                try {
                                    const response =
                                        await fetch(
                                            `${API_BASE_URL}/children/${childId}/classes/${subject.subject_id ?? subject.id}/session`,
                                            {
                                                credentials:
                                                    "include",
                                            }
                                        );

                                    if (
                                        !response.ok
                                    ) {
                                        return [
                                            String(
                                                subject.subject_id ??
                                                    subject.id
                                            ),
                                            null,
                                        ];
                                    }

                                    const sessionState =
                                        await response.json();

                                    return [
                                        String(
                                            subject.subject_id ??
                                                subject.id
                                        ),
                                        sessionState,
                                    ];
                                } catch (
                                    error
                                ) {
                                    return [
                                        String(
                                            subject.subject_id ??
                                                subject.id
                                        ),
                                        null,
                                    ];
                                }
                            }
                        )
                    );

                if (cancelled) return;

                setLiveSessionStates(
                    Object.fromEntries(
                        sessionResults.filter(
                            ([
                                ,
                                sessionState,
                            ]) =>
                                Boolean(
                                    sessionState
                                )
                        )
                    )
                );
            };

        loadLiveSessions();

        const pollingInterval =
            window.setInterval(
                loadLiveSessions,
                2000
            );

        return () => {
            cancelled = true;

            window.clearInterval(
                pollingInterval
            );
        };
    }, [childId, childSubjects]);

    const todayDateString =
        formatLocalDate(new Date());
    const upcomingImportantItems =
        React.useMemo(() => {
            const importantTypes =
                new Set([
                    "assignment",
                    "quiz",
                    "test",
                    "project",
                ]);

            const selectedSubjectId =
                selectedSubject
                    ? String(
                        selectedSubject.subject_id ??
                            selectedSubject.id
                    )
                    : "";

            return events
                .filter((event) => {
                    const eventChildId =
                        event?.child_id ??
                        event?.childId;

                    const eventSubjectId =
                        String(
                            event?.subject_id ??
                                event?.subjectId ??
                                ""
                        );

                    const eventDate =
                        getEventDate(event);

                    const eventType =
                        String(
                            event?.type ||
                                event?.quest_type ||
                                ""
                        ).toLowerCase();

                    return (
                        String(eventChildId) ===
                            String(childId) &&
                        (!selectedSubjectId ||
                            eventSubjectId ===
                                selectedSubjectId) &&
                        Boolean(eventDate) &&
                        eventDate >=
                            todayDateString &&
                        importantTypes.has(
                            eventType
                        ) &&
                        !event?.archived &&
                        event?.status !==
                            "completed"
                    );
                })
                .sort((a, b) =>
                    getEventDate(a).localeCompare(
                        getEventDate(b)
                    )
                )
                .slice(0, 6);
        }, [
            events,
            childId,
            selectedSubject,
            todayDateString,
        ]);

    const allChildQuestItems = React.useMemo(() => {
        const apiQuests =
            Array.isArray(quests)
                ? quests
                : [];

        const eventQuests =
            Array.isArray(events)
                ? events.filter((event) =>
                    CLASSROOM_QUEST_TYPES.includes(
                        String(
                            event?.type || ""
                        ).toLowerCase()
                    )
                )
                : [];
        console.log(
            "CHILD QUEST SOURCE DEBUG",
            {
                apiQuestCount: apiQuests.length,
                eventQuestCount: eventQuests.length,
                apiQuests,
                eventQuests,
            }
        );
        const mergedItems = new Map();

        apiQuests.forEach((quest) => {
            const itemId =
                quest?.quest_id ??
                quest?.event_id ??
                quest?.id;

            const fallbackKey = [
                quest?.title ??
                    quest?.name ??
                    "",
                quest?.subject_id ??
                    quest?.subjectId ??
                    quest?.class_id ??
                    quest?.classId ??
                    quest?.subject_name ??
                    "",
            ].join("|");

            const uniqueKey =
                itemId != null
                    ? String(itemId)
                    : fallbackKey;

            mergedItems.set(
                uniqueKey,
                quest
            );
        });

        eventQuests.forEach((event) => {
            const itemId =
                event?.quest_id ??
                event?.event_id ??
                event?.id;

            const fallbackKey = [
                event?.title ??
                    event?.name ??
                    "",
                event?.subject_id ??
                    event?.subjectId ??
                    event?.class_id ??
                    event?.classId ??
                    event?.subject_name ??
                    "",
            ].join("|");

            const uniqueKey =
                itemId != null
                    ? String(itemId)
                    : fallbackKey;

            const existingItem =
                mergedItems.get(
                    uniqueKey
                ) || {};

            mergedItems.set(
                uniqueKey,
                {
                    ...existingItem,
                    ...event,

                    materials:
                        event?.materials ??
                        existingItem?.materials,

                    breakdown:
                        event?.breakdown ??
                        existingItem?.breakdown,

                    rubric:
                        event?.rubric ??
                        existingItem?.rubric,
                }
            );
        });
            console.log(
                "CHILD MERGED QUEST ITEMS",
                Array.from(
                    mergedItems.values()
                )
            );
                return Array.from(
                    mergedItems.values()
                );
            }, [
                quests,
                events,
            ]);

            const visibleClassQuests = React.useMemo(() => {
        if (!selectedSubject) {
            return [];
        }

    const selectedSubjectId =
        selectedSubject.subject_id ??
        selectedSubject.id;

    return questsForClass(allChildQuestItems, childId, selectedSubjectId)
        .sort((a, b) => {
            const firstDate =
                a.due_date ||
                a.date_start ||
                a.date ||
                "";

            const secondDate =
                b.due_date ||
                b.date_start ||
                b.date ||
                "";

            return firstDate.localeCompare(
                secondDate
            );
        });
}, [
    allChildQuestItems,
    selectedSubject,
    childId,
]);
console.log(
    "CHILD SUBJECT ID DEBUG",
    {
        selectedSubject,
        questSubjects:
            Array.from(
                new Map(
                    allChildQuestItems.map(
                        (quest) => [
                            [
                                quest?.subject_id ??
                                    quest?.subjectId ??
                                    quest?.class_id ??
                                    quest?.classId ??
                                    "",
                                quest?.subject_name ??
                                    quest?.subjectName ??
                                    "",
                            ].join("|"),
                            {
                                subject_id:
                                    quest?.subject_id ??
                                    null,
                                subjectId:
                                    quest?.subjectId ??
                                    null,
                                class_id:
                                    quest?.class_id ??
                                    null,
                                classId:
                                    quest?.classId ??
                                    null,
                                subject_name:
                                    quest?.subject_name ??
                                    quest?.subjectName ??
                                    null,
                            },
                        ]
                    )
                ).values()
            ),
    }
);
const activeClassQuests =
    visibleClassQuests.filter(
        (quest) =>
            ![
                "completed",
                "graded",
                "approved",
                "archived",
            ].includes(
                String(
                    quest.status || ""
                ).toLowerCase()
            )
    );

const completedClassQuests =
    visibleClassQuests.filter(
        (quest) =>
            [
                "completed",
                "graded",
                "approved",
                "archived",
            ].includes(
                String(
                    quest.status || ""
                ).toLowerCase()
            )
    );   
    const getChildQuestStatus = (quest) =>
        String(
            quest?.status || "open"
        ).toLowerCase();

    const isChildQuestCompleted = (quest) =>
        [
            "completed",
            "graded",
            "approved",
            "archived",
        ].includes(
            getChildQuestStatus(quest)
        );

    const isChildQuestPending = (quest) =>
        [
            "pending",
            "submitted",
            "pending_approval",
            "awaiting_approval",
        ].includes(
            getChildQuestStatus(quest)
        );

    const getChildQuestDueDate = (quest) =>
        quest?.due_date ||
        quest?.date_end ||
        quest?.date_start ||
        quest?.date ||
        "";

    const getChildQuestCompletedDate = (quest) => {
        const completedValue =
            quest?.completed_at ||
            quest?.completion_date ||
            quest?.approved_at ||
            "";

        return completedValue
            ? String(completedValue).slice(0, 10)
            : "";
    };

    const todayQuestDate =
        formatLocalDate(new Date());

    const childDueTodayQuests =
        visibleClassQuests
            .filter((quest) => {
                if (
                    isChildQuestCompleted(quest) ||
                    isChildQuestPending(quest)
                ) {
                    return false;
                }

                return (
                    getChildQuestDueDate(quest) ===
                    todayQuestDate
                );
            })
            .sort((a, b) =>
                String(
                    a.due_time || "23:59"
                ).localeCompare(
                    String(
                        b.due_time || "23:59"
                    )
                )
            );

    const childLateQuests =
        visibleClassQuests
            .filter((quest) => {
                if (
                    isChildQuestCompleted(quest) ||
                    isChildQuestPending(quest) ||
                    quest?.excused
                ) {
                    return false;
                }

                const dueDate =
                    getChildQuestDueDate(quest);

                return (
                    dueDate &&
                    dueDate < todayQuestDate
                );
            })
            .sort((a, b) =>
                getChildQuestDueDate(a).localeCompare(
                    getChildQuestDueDate(b)
                )
            );

    const childCompletedTodayQuests =
        visibleClassQuests.filter(
            (quest) =>
                isChildQuestCompleted(quest) &&
                getChildQuestCompletedDate(
                    quest
                ) === todayQuestDate
        );

    const childPendingApprovalQuests =
        visibleClassQuests.filter(
            (quest) =>
                isChildQuestPending(quest)
        );

    const childComingWeekEnd =
        new Date();

    childComingWeekEnd.setDate(
        childComingWeekEnd.getDate() + 7
    );

    const childComingWeekEndString =
        formatLocalDate(
            childComingWeekEnd
        );

    const childComingUpThisWeekQuests =
        visibleClassQuests
            .filter((quest) => {
                if (
                    isChildQuestCompleted(quest) ||
                    isChildQuestPending(quest)
                ) {
                    return false;
                }

                const dueDate =
                    getChildQuestDueDate(quest);

                if (!dueDate) {
                    return false;
                }

                return (
                    dueDate > todayQuestDate &&
                    dueDate <=
                        childComingWeekEndString
                );
            })
            .sort((a, b) =>
                getChildQuestDueDate(a).localeCompare(
                    getChildQuestDueDate(b)
                )
            );

    const childNextTestQuiz =
        visibleClassQuests
            .filter((quest) => {
                const questType =
                    String(
                        quest?.type ||
                            quest?.quest_type ||
                            ""
                    ).toLowerCase();

                if (
                    ![
                        "test",
                        "quiz",
                    ].includes(
                        questType
                    )
                ) {
                    return false;
                }

                if (
                    isChildQuestCompleted(
                        quest
                    )
                ) {
                    return false;
                }

                const dueDate =
                    getChildQuestDueDate(
                        quest
                    );

                return (
                    dueDate &&
                    dueDate >=
                        todayQuestDate
                );
            })
            .sort((a, b) =>
                getChildQuestDueDate(a).localeCompare(
                    getChildQuestDueDate(b)
                )
            )[0] || null;
        useClassroomDashboard({ role: "child", children: authenticatedChild ? [authenticatedChild] : [], subjects });
        const calendarEvents =
            React.useMemo(() => {
        const selectedSubjectId =
            selectedSubject
                ? String(
                      selectedSubject.subject_id ??
                          selectedSubject.id
                  )
                : "";

        return groupLessonCalendarEvents(events, {collapseAssignments: true}).filter((event) => {
            const eventChildId =
                event?.child_id ??
                event?.childId;

            const eventSubjectId =
                String(
                    event?.subject_id ??
                        event?.subjectId ??
                        ""
                );

            return (
                String(eventChildId) ===
                    String(childId) &&
                (!selectedSubjectId ||
                    eventSubjectId ===
                        selectedSubjectId) &&
                !event?.archived
            );
        });
    }, [
        events,
        childId,
        selectedSubject,
    ]);

const getCalendarEventsForDate = (
    dateString
) =>
    calendarEvents
        .filter(
            (event) =>
                classroomEventOnDate(event, dateString)
        )
        .map((event) => ({ ...event, ...event.assignment_time_slots?.[dateString] }))
        .sort(
            (firstEvent, secondEvent) =>
                (timeToMinutes(
                    getEventStartTime(
                        firstEvent
                    )
                ) ?? 99999) -
                (timeToMinutes(
                    getEventStartTime(
                        secondEvent
                    )
                ) ?? 99999)
        );
    const parseLocalDate = (dateString) => {
    if (!dateString) {
        return new Date();
    }

    const [
        year,
        month,
        day,
    ] = String(dateString)
        .split("-")
        .map(Number);

    return new Date(
        year,
        month - 1,
        day
    );
};

const getWeekDates = (
    dateString
) => {
    const selectedDate =
        parseLocalDate(dateString);

    const dayOfWeek =
        selectedDate.getDay();

    const weekStart =
        new Date(selectedDate);

    weekStart.setDate(
        selectedDate.getDate() -
            dayOfWeek
    );

    return Array.from(
        { length: 7 },
        (_, index) => {
            const date =
                new Date(weekStart);

            date.setDate(
                weekStart.getDate() +
                    index
            );

            return formatLocalDate(
                date
            );
        }
    );
};

const getMonthDates = (
    monthDate
) => {
    const year =
        monthDate.getFullYear();

    const month =
        monthDate.getMonth();

    const firstDay =
        new Date(
            year,
            month,
            1
        );

    const firstDayOffset =
        firstDay.getDay();

    const gridStart =
        new Date(
            year,
            month,
            1 - firstDayOffset
        );

    return Array.from(
        { length: 42 },
        (_, index) => {
            const date =
                new Date(gridStart);

            date.setDate(
                gridStart.getDate() +
                    index
            );

            return {
                dateString:
                    formatLocalDate(
                        date
                    ),
                inCurrentMonth:
                    date.getMonth() ===
                    month,
            };
        }
    );
};

const calendarWeekDates =
    getWeekDates(
        calendarSelectedDate
    );

const calendarMonthDates =
    getMonthDates(
        calendarMonthDate
    );
    const todaySessions =
        React.useMemo(() => {
            const sessionGroups =
                new Map();

            events
                .filter((event) => {
                    const eventChildId =
                        event?.child_id ??
                        event?.childId;

                    return (
                        String(
                            eventChildId
                        ) ===
                            String(childId) &&
                        isSessionEvent(
                            event
                        ) &&
                        getEventDate(
                            event
                        ) ===
                            todayDateString &&
                        !event?.archived
                    );
                })
                .forEach((event) => {
                    const eventSubjectId =
                        String(
                            event.subject_id ??
                                event.subjectId ??
                                ""
                        );

                    const groupKey =
                        event.session_flow_id ||
                        event.session_id ||
                        `${eventSubjectId}-${getEventDate(
                            event
                        )}`;

                    if (
                        !sessionGroups.has(
                            groupKey
                        )
                    ) {
                        sessionGroups.set(
                            groupKey,
                            {
                                id: groupKey,
                                subjectId:
                                    eventSubjectId,
                                blocks: [],
                            }
                        );
                    }

                    sessionGroups
                        .get(groupKey)
                        .blocks.push(event);
                });

            const groupedSessions =
                Array.from(
                    sessionGroups.values()
                ).map((sessionGroup) => {
                    const sortedBlocks = [
                        ...sessionGroup.blocks,
                    ].sort(
                        (
                            firstBlock,
                            secondBlock
                        ) =>
                            (timeToMinutes(
                                getEventStartTime(
                                    firstBlock
                                )
                            ) ?? 99999) -
                            (timeToMinutes(
                                getEventStartTime(
                                    secondBlock
                                )
                            ) ?? 99999)
                    );

                    const firstBlock =
                        sortedBlocks[0] ||
                        {};

                    const finalBlock =
                        sortedBlocks[
                            sortedBlocks.length -
                                1
                        ] || {};

                    const subject =
                        subjectsById[
                            String(
                                sessionGroup.subjectId
                            )
                        ] || null;

                    return {
                        ...sessionGroup,
                        subject,
                        blocks: sortedBlocks,
                        liveState:
                            liveSessionStates[
                                String(
                                    sessionGroup.subjectId
                                )
                            ] || null,
                        startTime:
                            getEventStartTime(
                                firstBlock
                            ),
                        endTime:
                            getEventEndTime(
                                finalBlock
                            ),
                    };
                });

            const includedSubjectIds =
                new Set(
                    groupedSessions.map(
                        (session) =>
                            String(
                                session.subjectId
                            )
                    )
                );

            Object.entries(
                liveSessionStates
            ).forEach(
                ([
                    liveSubjectId,
                    liveState,
                ]) => {
                    if (
                        !liveState ||
                        ![
                            "running",
                            "paused",
                        ].includes(
                            liveState.status
                        ) ||
                        includedSubjectIds.has(
                            String(
                                liveSubjectId
                            )
                        )
                    ) {
                        return;
                    }

                    groupedSessions.push({
                        id: `live-${liveSubjectId}`,
                        subjectId:
                            liveSubjectId,
                        subject:
                            subjectsById[
                                String(
                                    liveSubjectId
                                )
                            ] || null,
                        blocks: [],
                        liveState,
                        startTime: "",
                        endTime: "",
                    });
                }
            );

            return groupedSessions.sort(
                (
                    firstSession,
                    secondSession
                ) =>
                    (timeToMinutes(
                        firstSession.startTime
                    ) ?? 99999) -
                    (timeToMinutes(
                        secondSession.startTime
                    ) ?? 99999)
            );
        }, [
            childId,
            events,
            liveSessionStates,
            subjectsById,
            todayDateString,
        ]);

    const selectedClassSession =
        React.useMemo(() => {
            if (!selectedSubject) {
                return null;
            }

            const selectedSubjectId =
                selectedSubject.subject_id ??
                selectedSubject.id;

            const todaySession =
                todaySessions.find(
                    (session) =>
                        String(
                            session.subjectId
                        ) ===
                        String(
                            selectedSubjectId
                        )
                );

            if (todaySession) {
                return todaySession;
            }

            return {
                id: `selected-${selectedSubjectId}`,
                subjectId:
                    String(
                        selectedSubjectId
                    ),
                subject:
                    selectedSubject,
                blocks: [],
                liveState:
                    liveSessionStates[
                        String(
                            selectedSubjectId
                        )
                    ] || null,
                startTime: "",
                endTime: "",
            };
        }, [
            liveSessionStates,
            selectedSubject,
            todaySessions,
        ]);

    const childName =
        child?.name?.trim() ||
        child?.firstName?.trim() ||
        "Student";

    const getSessionSteps = (
        session
    ) => {
        if (
            session?.liveState?.steps
                ?.length
        ) {
            return session.liveState.steps;
        }

        return (
            session?.blocks?.map(
                (block, index) => ({
                    step_id:
                        block.step_id ||
                        block.id ||
                        `${session.id}-${index}`,
                    title:
                        block.title ||
                        block.lesson_title ||
                        `Section ${
                            index + 1
                        }`,
                    description:
                        block.description ||
                        block.notes ||
                        "",
                    planned_minutes:
                        Number(
                            block.planned_minutes ??
                                block.duration_minutes ??
                                0
                        ) || 0,
                    actual_seconds:
                        Number(
                            block.actual_seconds ??
                                0
                        ) || 0,
                    completed:
                        Boolean(
                            block.completed
                        ),
                    skipped:
                        Boolean(
                            block.skipped
                        ),
                })
            ) || []
        );
    };

    const getSessionTimerSeconds = (
        session
    ) => {
        const liveState =
            session?.liveState;

        if (!liveState) return 0;

        const baseSeconds =
            Number(
                liveState.base_seconds
            ) || 0;

        if (
            liveState.status ===
                "running" &&
            liveState.started_at
        ) {
            return (
                baseSeconds +
                getElapsedSince(
                    liveState.started_at
                )
            );
        }

        return baseSeconds;
    };

    const getActiveStep = (
        session
    ) => {
        const steps =
            getSessionSteps(session);

        const activeStepId =
            session?.liveState
                ?.active_step_id;

        if (activeStepId) {
            return (
                steps.find(
                    (step) =>
                        String(
                            step.step_id
                        ) ===
                        String(
                            activeStepId
                        )
                ) || null
            );
        }

        return (
            steps.find(
                (step) =>
                    !step.completed &&
                    !step.skipped
            ) || null
        );
    };

    const getActiveStepSeconds = (
        session,
        activeStep
    ) => {
        if (!activeStep) return 0;

        let elapsedSeconds =
            Number(
                activeStep.actual_seconds
            ) || 0;

        if (
            session?.liveState?.status ===
                "running" &&
            String(
                session.liveState
                    .active_step_id
            ) ===
                String(
                    activeStep.step_id
                ) &&
            session.liveState
                .active_step_started_at
        ) {
            elapsedSeconds +=
                getElapsedSince(
                    session.liveState
                        .active_step_started_at
                );
        }

        return elapsedSeconds;
    };

    const getNextStep = (
        session,
        activeStep
    ) => {
        if (!activeStep) return null;

        const steps =
            getSessionSteps(session);

        const activeIndex =
            steps.findIndex(
                (step) =>
                    String(
                        step.step_id
                    ) ===
                    String(
                        activeStep.step_id
                    )
            );

        if (activeIndex < 0) {
            return null;
        }

        return (
            steps
                .slice(activeIndex + 1)
                .find(
                    (step) =>
                        !step.completed &&
                        !step.skipped
                ) || null
        );
    };

    const getOverallProgress = (
        session
    ) => {
        const steps =
            getSessionSteps(session);

        const plannedSeconds =
            steps.reduce(
                (total, step) =>
                    total +
                    (Number(
                        step.planned_minutes
                    ) || 0) *
                        60,
                0
            );

        if (plannedSeconds <= 0) {
            return 0;
        }

        const elapsedSeconds =
            steps.reduce(
                (total, step) =>
                    total +
                    getActiveStepSeconds(
                        session,
                        step
                    ),
                0
            );

        return Math.min(
            100,
            Math.max(
                0,
                Math.round(
                    (elapsedSeconds /
                        plannedSeconds) *
                        100
                )
            )
        );
    };

    const renderLiveSessionPanel = (
        session
    ) => {
        const subject =
            session?.subject;

        const activeColor =
            subject?.color ||
            "#ffd166";

        const liveStatus =
            session?.liveState
                ?.status || "waiting";

        const timerSeconds =
            getSessionTimerSeconds(
                session
            );

        const activeStep =
            getActiveStep(session);

        const activeStepSeconds =
            getActiveStepSeconds(
                session,
                activeStep
            );

        const activeStepPlannedSeconds =
            (Number(
                activeStep?.planned_minutes
            ) || 0) * 60;

        const activeStepRemaining =
            Math.max(
                0,
                activeStepPlannedSeconds -
                    activeStepSeconds
            );

        const nextStep =
            getNextStep(
                session,
                activeStep
            );

        const overallProgress =
            getOverallProgress(session);

        const statusText =
            liveStatus === "running"
                ? "Class In Session"
                : liveStatus ===
                  "paused"
                ? "Class Paused"
                : liveStatus ===
                  "completed"
                ? "Class Completed"
                : "Class Not In Session";

        return (
            <section
                className="
                    relative overflow-hidden
                    rounded-[32px]
                    border border-[#ffd166]/40
                    bg-black
                    p-6
                    shadow-[0_0_35px_rgba(255,209,102,0.10)]
                    backdrop-blur-sm
                "
                style={{
                    backgroundColor:
                        "#080b14",
                }}
            >
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p
                            className="text-xs font-semibold uppercase tracking-[0.2em]"
                            style={{
                                color: activeColor,
                            }}
                        >
                            {subject?.name ||
                                "Class Session"}
                        </p>

                        {(session.startTime ||
                            session.endTime) && (
                            <p className="mt-1 text-sm text-[#b0b8d6]">
                                {session.startTime
                                    ? formatCalendarTime(
                                          session.startTime
                                      )
                                    : ""}

                                {session.endTime
                                    ? ` – ${formatCalendarTime(
                                          session.endTime
                                      )}`
                                    : ""}
                            </p>
                        )}
                    </div>

                    {subjectId !==
                        String(
                            session.subjectId
                        ) && (
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    `/child/classroom/${childId}/${session.subjectId}`
                                )
                            }
                            className={
                                purpleActionBtn
                            }
                        >
                            <BookOpen
                                size={16}
                            />
                            Enter Class
                        </button>
                    )}
                </div>

                <div className="w-full">
                    <div
                        className="w-full rounded-3xl border px-8 py-6 text-center transition-all duration-300"
                        style={{
                            color: activeColor,
                            borderColor:
                                activeColor,
                            boxShadow: `0 0 28px ${activeColor}66`,
                            backgroundColor:
                                "#00000088",
                        }}
                    >
                        <div className="text-xs uppercase tracking-[0.25em] text-[#b0b8d6]">
                            {statusText}
                        </div>

                        <div className="mt-3 font-display text-6xl">
                            {formatTimer(
                                timerSeconds
                            )}
                        </div>

                        <div
                            className="mt-3 font-display text-2xl tracking-wide"
                            style={{
                                color: activeColor,
                                textShadow: `0 0 14px ${activeColor}88`,
                            }}
                        >
                            {activeStep?.title ||
                                (liveStatus ===
                                "completed"
                                    ? "Session Complete"
                                    : "No Active Section")}
                        </div>
                    

                        {activeStep && (
                            <div
                                className="mt-2 flex flex-wrap items-center justify-center gap-2 text-sm"
                                style={{
                                    color: activeColor,
                                }}
                            >
                                {activeStepPlannedSeconds >
                                    0 && (
                                    <span>
                                        Remaining:{" "}
                                        {formatTimer(
                                            activeStepRemaining
                                        )}
                                    </span>
                                )}

                                {nextStep && (
                                    <span className="rounded-full border border-[#ffd166]/40 bg-black px-3 py-1 text-xs text-[#ffd166] shadow-[0_0_12px_rgba(255,209,102,0.16)]">
                                        Next:{" "}
                                        {nextStep.title ||
                                            "Untitled Section"}
                                    </span>
                                )}

                                {getSessionSteps(
                                    session
                                ).length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setLessonFlowModalSession(
                                                session
                                            )
                                        }
                                        className="rounded-full border border-[#ffd166]/40 bg-black px-3 py-1 text-xs text-[#ffd166] shadow-[0_0_12px_rgba(255,209,102,0.16)] transition-all duration-200 hover:bg-[#ffd166]/[0.06]"
                                        title="View Full Lesson Flow"
                                    >
                                        ⋯
                                    </button>
                                )}
                            </div>
                        )}

                        {activeStep && (
                            <div className="mx-auto mt-5 w-full max-w-xl">
                                <div className="h-5 overflow-hidden rounded-full border border-[#ffd166]/50 bg-black shadow-[0_0_14px_rgba(255,209,102,0.18)]">
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width: `${
                                                activeStepPlannedSeconds >
                                                0
                                                    ? Math.min(
                                                          100,
                                                          Math.round(
                                                              (activeStepSeconds /
                                                                  activeStepPlannedSeconds) *
                                                                  100
                                                          )
                                                      )
                                                    : 0
                                            }%`,
                                            backgroundColor:
                                                activeColor,
                                            boxShadow: `0 0 18px ${activeColor}99`,
                                        }}
                                    />
                                </div>

                                <div className="mt-3 w-full overflow-hidden py-2">
                                    <div
                                        className="whitespace-nowrap animate-pulse"
                                        style={{
                                            color: activeColor,
                                            transform: `translateX(-${
                                                (timerSeconds *
                                                    2) %
                                                300
                                            }%)`,
                                            transition:
                                                "transform 1s linear",
                                        }}
                                    >
                                        {Array(10)
                                            .fill(
                                                activeStep.description ||
                                                    "No session description yet."
                                            )
                                            .join(
                                                "   •   "
                                            )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {session && (
                            <div className="mt-5 flex flex-col items-center">
                                <div className="relative h-24 w-24 overflow-hidden rounded-full border border-[#ffd166]/60 bg-black shadow-[0_0_22px_rgba(255,209,102,0.25)]">
                                    <img
                                        src="/images/moon.gif"
                                        alt="Moon progress background"
                                        className="absolute inset-0 h-full w-full rounded-full object-cover"
                                    />

                                    <div
                                        className="absolute inset-0 rounded-full"
                                        style={{
                                            maskImage: `conic-gradient(
                                                from 0deg,
                                                black 0deg,
                                                black ${
                                                    overallProgress *
                                                    3.6
                                                }deg,
                                                transparent ${
                                                    overallProgress *
                                                    3.6
                                                }deg,
                                                transparent 360deg
                                            )`,
                                            WebkitMaskImage: `conic-gradient(
                                                from 0deg,
                                                black 0deg,
                                                black ${
                                                    overallProgress *
                                                    3.6
                                                }deg,
                                                transparent ${
                                                    overallProgress *
                                                    3.6
                                                }deg,
                                                transparent 360deg
                                            )`,
                                        }}
                                    >
                                        <img
                                            src="/images/sun.gif"
                                            alt="Sun progress reveal"
                                            className="absolute inset-0 h-full w-full rounded-full object-cover scale-125"
                                        />
                                    </div>

                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="rounded-full bg-black/75 px-2 py-1 text-xs font-bold text-[#ffd166]">
                                            {
                                                overallProgress
                                            }
                                            %
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        );
    };

    const renderHomeNavigation = () => null;

    const renderTodayPanel = () => {
        const visibleTodaySessions =
            subjectId
                ? selectedClassSession
                    ? [selectedClassSession]
                    : []
                : todaySessions;

        return (
            <>
            <section className="rounded-[32px] border border-[#ffd166]/30 bg-black p-4 shadow-[0_0_30px_rgba(168,85,247,0.10)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                        Classroom Navigation
                    </p>

                    <h2 className="mt-1 font-display text-2xl text-[#ffd166]">
                        {selectedSubject?.name ||
                            `${childName}'s Classes`}
                    </h2>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button type="button" className={compactClassroomButtonClass(activePanel === "bulletin")} onClick={() => setActivePanel("bulletin")}>Bulletin Board</button>
                    <button
                        type="button"
                        onClick={() =>
                            setActivePanel("lessonStage")
                        }
                        className={compactClassroomButtonClass(
                            activePanel === "lessonStage"
                        )}
                    >
                        <Clock size={16} />
                        Lesson Stage
                    </button>

                    <button
                        type="button"
                        onClick={() =>
                            setActivePanel("calendar")
                        }
                        className={compactClassroomButtonClass(
                            activePanel === "calendar"
                        )}
                    >
                        <CalendarDays size={16} />
                        Calendar
                    </button>
                <button
    type="button"
    onClick={() =>
        setActivePanel("archivum")
    }
    className={compactClassroomButtonClass(
        activePanel === "archivum"
    )}
>
    <BookOpen size={16} />
    Curriculum & Resources
</button>

<button
    type="button"
    onClick={() =>
        setActivePanel("quests")
    }
    className={compactClassroomButtonClass(
        activePanel === "quests"
    )}
>
    <GraduationCap size={16} />
    Quests
</button>
                    <button
                        type="button"
                        onClick={() =>
                            setActivePanel("classes")
                        }
                        className={compactClassroomButtonClass(
                            activePanel === "classes"
                        )}
                    >
                        <GraduationCap size={16} />
                        Other Classes
                    </button>

                    <button
                        type="button"
                        onClick={() =>
                            setActivePanel("messages")
                        }
                        className={compactClassroomButtonClass(
                            activePanel === "messages"
                        )}
                    >
                        <Mail size={16} />
                        Messages
                    </button>
                </div>
            </div>
        </section>
                <div
                    className="mt-6 grid items-start gap-0"
                    style={
                        isClassroomNotesOpen
                            ? {
                                gridTemplateColumns:
                                    `minmax(0, 1fr) 10px ${layout.workspaceWidth}px`,
                            }
                            : {
                                gridTemplateColumns:
                                    "minmax(0, 1fr) auto",
                            }
                    }
                >
                    <div className="min-w-0">
                        <section
                            className="
                                rounded-[32px]
                                border border-[#ffd166]/40
                                bg-black
                                p-6
                                shadow-[0_0_35px_rgba(255,209,102,0.10)]
                            "
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.24em] text-[#d8b4fe]">
                                        Classroom Dashboard
                                    </p>

                                    <h3 className="mt-1 font-display text-3xl text-[#ffd166]">
                                        {activePanel === "lessonStage"
                                            ? "Lesson Stage"
                                            : activePanel === "bulletin"
                                            ? "Bulletin Board"
                                            : activePanel === "calendar"
                                            ? "Calendar"
                                            : activePanel === "quests"
                                            ? "Quests"
                                            : activePanel === "archivum"
                                            ? "Curriculum & Resources"
                                            : activePanel === "classes"
                                            ? "Other Classes"
                                            : activePanel === "messages"
                                            ? "Messages"
                                            : "Classroom"}
                                    </h3>

<p className="mt-1 text-sm text-[#b0b8d6]">
    {activePanel === "lessonStage"
        ? "Class lessons and classroom tools."
        : activePanel === "bulletin"
        ? "To do, due today, late work, completions, and pending approvals."
        : activePanel === "calendar"
        ? "View this class schedule and synchronized classroom events."
        : activePanel === "quests"
        ? "View active, upcoming, completed, and submitted class quests."
        : activePanel === "archivum"
        ? "Open curriculum, resources, folders, and class materials."
        : activePanel === "classes"
        ? "Move between this child's classrooms."
        : activePanel === "messages"
        ? "View classroom and parent–child messages."
        : "Classroom tools and information."}
</p>
                                </div>
                            </div>
                        {activePanel !== "lessonStage" && (
                            <div className="mt-6">
                                {renderClassroomDashboardContent()}
                            </div>
                        )}
                            <div className="mt-6">
                            {activePanel === "bulletin" && <>
                                <div className="rounded-2xl border border-[#c084fc]/30 bg-[#160d26]/45 p-4">
                                    <div className="flex items-center justify-between gap-3">
                                        <h4 className="font-display text-xl text-[#e9d5ff]">
                                            To Do
                                        </h4>

                                        {activeClassQuests.length > 0 && (
                                            <span className="rounded-full border border-[#c084fc]/40 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#e9d5ff]">
                                                {activeClassQuests.length}
                                            </span>
                                        )}
                                    </div>

                                    {activeClassQuests.length === 0 ? (
                                        <p className="mt-2 text-sm text-[#b0b8d6]">
                                            No active quests for this class.
                                        </p>
                                    ) : (
                                        <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                                            {activeClassQuests
                                                .slice(0, 6)
                                                .map((quest) => (
                                                    <div
                                                        key={
                                                            quest.quest_id ??
                                                            quest.event_id ??
                                                            quest.id
                                                        }
                                                        className="
                                                            rounded-xl
                                                            border
                                                            border-[#c084fc]/20
                                                            bg-black/40
                                                            p-3
                                                        "
                                                    >
                                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                                            {quest.title ||
                                                                quest.name ||
                                                                "Untitled Quest"}
                                                        </p>

                                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[#9da6c3]">
                                                            {(quest.date_start ||
                                                                quest.date) && (
                                                                <span>
                                                                    Starts:{" "}
                                                                    {quest.date_start ||
                                                                        quest.date}
                                                                </span>
                                                            )}

                                                            {quest.due_date && (
                                                                <span className="text-[#ffd166]">
                                                                    Due: {quest.due_date}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <h4 className="font-display text-xl text-[#ffd166]">
                                                Due Today
                                            </h4>

                                            {childDueTodayQuests.length > 0 && (
                                                <span className="rounded-full border border-[#ffd166]/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#ffd166]">
                                                    {childDueTodayQuests.length}
                                                </span>
                                            )}
                                        </div>

                                        {childDueTodayQuests.length === 0 ? (
                                            <p className="mt-2 text-sm text-[#b0b8d6]">
                                                No open quests are due today.
                                            </p>
                                        ) : (
                                            <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                                                {childDueTodayQuests.map((quest) => (
                                                    <button
                                                        key={
                                                            quest.id ??
                                                            quest.quest_id ??
                                                            `${quest.title}-${getChildQuestDueDate(
                                                                quest
                                                            )}`
                                                        }
                                                        type="button"
                                                        onClick={() =>
                                                            setSelectedChildQuestItem(
                                                                quest
                                                            )
                                                        }
                                                        className="
                                                            block
                                                            w-full
                                                            rounded-xl
                                                            border
                                                            border-[#ffd166]/15
                                                            bg-black/40
                                                            p-3
                                                            text-left
                                                            transition
                                                            hover:border-[#ffd166]/45
                                                            hover:bg-[#ffd166]/5
                                                            hover:shadow-[0_0_18px_rgba(255,209,102,0.10)]
                                                        "
                                                    >
                                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                                            {quest.title ||
                                                                quest.name ||
                                                                "Untitled Quest"}
                                                        </p>

                                                        <p className="mt-1 text-xs text-[#ffd166]">
                                                            Due:{" "}
                                                            {getChildQuestDueDate(
                                                                quest
                                                            )}
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="rounded-2xl border border-red-400/30 bg-red-950/20 p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <h4 className="font-display text-xl text-red-200">
                                                Late
                                            </h4>

                                            {childLateQuests.length > 0 && (
                                                <span className="rounded-full border border-red-300/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-red-200">
                                                    {childLateQuests.length}
                                                </span>
                                            )}
                                        </div>

                                        {childLateQuests.length === 0 ? (
                                            <p className="mt-2 text-sm text-[#c4b5d8]">
                                                No late work.
                                            </p>
                                        ) : (
                                            <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                                                {childLateQuests.map((quest) => (
                                                    <button
                                                        key={
                                                            quest.id ??
                                                            quest.quest_id ??
                                                            `${quest.title}-${getChildQuestDueDate(
                                                                quest
                                                            )}`
                                                        }
                                                        type="button"
                                                        onClick={() =>
                                                            setSelectedChildQuestItem(
                                                                quest
                                                            )
                                                        }
                                                        className="
                                                            block
                                                            w-full
                                                            rounded-xl
                                                            border
                                                            border-red-300/15
                                                            bg-black/40
                                                            p-3
                                                            text-left
                                                            transition
                                                            hover:border-red-300/45
                                                            hover:bg-red-400/5
                                                        "
                                                    >
                                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                                            {quest.title ||
                                                                quest.name ||
                                                                "Untitled Quest"}
                                                        </p>

                                                        <p className="mt-1 text-xs text-red-200">
                                                            Due:{" "}
                                                            {getChildQuestDueDate(
                                                                quest
                                                            )}
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <h4 className="font-display text-xl text-[#ffd166]">
                                                Completed Today
                                            </h4>

                                            {childCompletedTodayQuests.length > 0 && (
                                                <span className="rounded-full border border-[#ffd166]/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#ffd166]">
                                                    {childCompletedTodayQuests.length}
                                                </span>
                                            )}
                                        </div>

                                        {childCompletedTodayQuests.length === 0 ? (
                                            <p className="mt-2 text-sm text-[#b0b8d6]">
                                                No quests completed today.
                                            </p>
                                        ) : (
                                            <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                                                {childCompletedTodayQuests.map((quest) => (
                                                    <button
                                                        key={
                                                            quest.id ??
                                                            quest.quest_id ??
                                                            `${quest.title}-${getChildQuestCompletedDate(
                                                                quest
                                                            )}`
                                                        }
                                                        type="button"
                                                        onClick={() =>
                                                            setSelectedChildQuestItem(
                                                                quest
                                                            )
                                                        }
                                                        className="
                                                            block
                                                            w-full
                                                            rounded-xl
                                                            border
                                                            border-[#ffd166]/15
                                                            bg-black/40
                                                            p-3
                                                            text-left
                                                            transition
                                                            hover:border-[#ffd166]/45
                                                            hover:bg-[#ffd166]/5
                                                            hover:shadow-[0_0_18px_rgba(255,209,102,0.10)]
                                                        "
                                                    >
                                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                                            {quest.title ||
                                                                quest.name ||
                                                                "Untitled Quest"}
                                                        </p>

                                                        <p className="mt-1 text-xs text-[#b0b8d6]">
                                                            Completed:{" "}
                                                            {getChildQuestCompletedDate(
                                                                quest
                                                            )}
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="rounded-2xl border border-[#c084fc]/30 bg-[#160d26]/45 p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <h4 className="font-display text-xl text-[#e9d5ff]">
                                                Pending Approval
                                            </h4>

                                            {childPendingApprovalQuests.length > 0 && (
                                                <span className="rounded-full border border-[#c084fc]/40 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#e9d5ff]">
                                                    {childPendingApprovalQuests.length}
                                                </span>
                                            )}
                                        </div>

                                        {childPendingApprovalQuests.length === 0 ? (
                                            <p className="mt-2 text-sm text-[#c4b5d8]">
                                                No work is waiting for approval.
                                            </p>
                                        ) : (
                                            <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                                                {childPendingApprovalQuests.map((quest) => (
                                                    <button
                                                        key={
                                                            quest.id ??
                                                            quest.quest_id ??
                                                            `${quest.title}-${quest.status}`
                                                        }
                                                        type="button"
                                                        onClick={() =>
                                                            setSelectedChildQuestItem(
                                                                quest
                                                            )
                                                        }
                                                        className="
                                                            block
                                                            w-full
                                                            rounded-xl
                                                            border
                                                            border-[#c084fc]/20
                                                            bg-black/40
                                                            p-3
                                                            text-left
                                                            transition
                                                            hover:border-[#c084fc]/60
                                                            hover:bg-[#2e1065]/25
                                                            hover:shadow-[0_0_18px_rgba(192,132,252,0.12)]
                                                        "
                                                    >
                                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                                            {quest.title ||
                                                                quest.name ||
                                                                "Untitled Quest"}
                                                        </p>

                                                        <p className="mt-1 text-xs text-[#d8b4fe]">
                                                            Status:{" "}
                                                            {quest.status ||
                                                                "Pending"}
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <h4 className="font-display text-xl text-[#ffd166]">
                                                Coming Up This Week
                                            </h4>

                                            {childComingUpThisWeekQuests.length > 0 && (
                                                <span className="rounded-full border border-[#ffd166]/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#ffd166]">
                                                    {childComingUpThisWeekQuests.length}
                                                </span>
                                            )}
                                        </div>

                                        {childComingUpThisWeekQuests.length === 0 ? (
                                            <p className="mt-2 text-sm text-[#b0b8d6]">
                                                No upcoming quests this week.
                                            </p>
                                        ) : (
                                            <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                                                {childComingUpThisWeekQuests.map((quest) => (
                                                    <button
                                                        key={
                                                            quest.id ??
                                                            quest.quest_id ??
                                                            `${quest.title}-${getChildQuestDueDate(
                                                                quest
                                                            )}`
                                                        }
                                                        type="button"
                                                        onClick={() =>
                                                            setSelectedChildQuestItem(
                                                                quest
                                                            )
                                                        }
                                                        className="
                                                            block
                                                            w-full
                                                            rounded-xl
                                                            border
                                                            border-[#ffd166]/15
                                                            bg-black/40
                                                            p-3
                                                            text-left
                                                            transition
                                                            hover:border-[#ffd166]/45
                                                            hover:bg-[#ffd166]/5
                                                            hover:shadow-[0_0_18px_rgba(255,209,102,0.10)]
                                                        "
                                                    >
                                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                                            {quest.title ||
                                                                quest.name ||
                                                                "Untitled Quest"}
                                                        </p>

                                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#b0b8d6]">
                                                            <span>
                                                                {String(
                                                                    quest.type ||
                                                                        quest.quest_type ||
                                                                        "Quest"
                                                                )}
                                                            </span>

                                                            <span className="text-[#ffd166]">
                                                                {getChildQuestDueDate(
                                                                    quest
                                                                )}
                                                            </span>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="rounded-2xl border border-[#c084fc]/30 bg-[#160d26]/45 p-4">
                                        <h4 className="font-display text-xl text-[#e9d5ff]">
                                            Next Test / Quiz
                                        </h4>

                                        {!childNextTestQuiz ? (
                                            <p className="mt-2 text-sm text-[#c4b5d8]">
                                                No upcoming test or quiz is scheduled.
                                            </p>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSelectedChildQuestItem(
                                                        childNextTestQuiz
                                                    )
                                                }
                                                className="
                                                    mt-3
                                                    block
                                                    w-full
                                                    rounded-xl
                                                    border
                                                    border-[#c084fc]/20
                                                    bg-black/40
                                                    p-3
                                                    text-left
                                                    transition
                                                    hover:border-[#c084fc]/60
                                                    hover:bg-[#2e1065]/25
                                                    hover:shadow-[0_0_18px_rgba(192,132,252,0.12)]
                                                "
                                            >
                                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                                    {String(
                                                        childNextTestQuiz.type ||
                                                            childNextTestQuiz.quest_type ||
                                                            "Test"
                                                    )}
                                                </p>

                                                <p className="mt-1 text-sm font-semibold text-[#fff3c4]">
                                                    {childNextTestQuiz.title ||
                                                        childNextTestQuiz.name ||
                                                        "Untitled Test / Quiz"}
                                                </p>

                                                <p className="mt-2 text-xs text-[#ffd166]">
                                                    {getChildQuestDueDate(
                                                        childNextTestQuiz
                                                    )}
                                                </p>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                </>}
                            {activePanel === "lessonStage" && (subjectId ? <LessonStagePanel role="child" childId={childId} subjectId={selectedSubject?.subject_id ?? selectedSubject?.id ?? subjectId} child={child} /> : visibleTodaySessions.map(session => <LessonStagePanel key={session.id} role="child" childId={childId} subjectId={session.subject?.subject_id ?? session.subject?.id} child={child} />))}
                            </div>
                        </section>
                    </div>

                    {isClassroomNotesOpen && (
                        <div
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize classroom workspace"
                            onPointerDown={
                                startClassroomWorkspaceResize
                            }
                            className="
                                hidden
                                md:flex
                                h-full
                                min-h-[480px]
                                cursor-col-resize
                                items-stretch
                                justify-center
                                group
                            "
                        >
                            <div
                                className="
                                    w-[2px]
                                    rounded-full
                                    bg-[#ffd166]/20
                                    transition-all
                                    duration-150
                                    group-hover:w-[4px]
                                    group-hover:bg-[#ffd166]/70
                                    group-hover:shadow-[0_0_14px_rgba(255,209,102,0.45)]
                                "
                            />
                        </div>
                    )}

                    <div className="min-w-0">
                            <ClassroomNotesPanel
                role="child"
                                childId={childId}
                                subjectId={
                                    selectedSubject?.subject_id ??
                                    selectedSubject?.id ??
                                    subjectId
                                }
                                subjectName={
                                    selectedSubject?.name ||
                                    "Class"
                                }
                                onOpenChange={
                                    setIsClassroomNotesOpen
                                }
                            />
                        </div>
                </div>
            </>
        );
    };

    const renderClassesPanel = () => (
        <section className="rounded-[32px] border border-[#ffd166]/40 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.10)]">
            <p className="text-xs uppercase tracking-[0.2em] text-[#ffd166]">
                Schola Minervae
            </p>

            <h2 className="mt-2 font-display text-3xl text-[#ffd166]">
                Classes
            </h2>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
                {childSubjects.map(
                    (subject) => {
                        const subjectColor =
                            subject.color ||
                            "#ffd166";

                        return (
                            <button
                                key={
                                    subject.id
                                }
                                type="button"
                                onClick={() =>
                                    navigate(
                                        `/child/classroom/${childId}/${subject.subject_id ?? subject.id}`
                                    )
                                }
                                className="rounded-[28px] border bg-[#080b14] p-5 text-left transition-all duration-200 hover:-translate-y-1"
                                style={{
                                    borderColor:
                                        subjectColor,
                                    boxShadow: `0 0 22px ${subjectColor}33`,
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    <BookOpen
                                        size={22}
                                        style={{
                                            color: subjectColor,
                                        }}
                                    />

                                    <h3
                                        className="font-display text-2xl"
                                        style={{
                                            color: subjectColor,
                                        }}
                                    >
                                        {subject.name}
                                    </h3>
                                </div>

                                <p className="mt-3 text-sm text-[#b0b8d6]">
                                    Enter the class to
                                    view its live session,
                                    lesson flow, quests,
                                    resources, and
                                    calendar.
                                </p>
                            </button>
                        );
                    }
                )}
            </div>
        </section>
    );

    const renderPlaceholderPanel = (
        title,
        description
    ) => (
        <section className="rounded-[32px] border border-[#ffd166]/40 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.10)]">
            <h2 className="font-display text-3xl text-[#ffd166]">
                {title}
            </h2>

            <p className="mt-3 text-sm text-[#b0b8d6]">
                {description}
            </p>
        </section>
    );

    const renderClassroomDashboardContent = () => {
        if (activePanel === "classes") {
            return renderClassesPanel();
        }
    if (activePanel === "quests") {
    return (
        <section
            className="
                min-h-[480px]
                resize-y
                overflow-auto
                rounded-[32px]
                border border-[#ffd166]/40
                bg-black
                p-6
                shadow-[0_0_35px_rgba(255,209,102,0.10)]
            "
        >
            <div className="mb-6">
                <p className="text-xs uppercase tracking-[0.3em] text-[#d8b4fe]">
                    Class Quests
                </p>

                <h2 className="mt-2 font-display text-3xl text-[#ffd166]">
                    Quests
                </h2>

                <p className="mt-2 text-sm text-[#b0b8d6]">
                    Your active and completed quests for this class.
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <div
                    className="
                        rounded-2xl
                        border border-[#ffd166]/20
                        bg-[#080b14]
                        p-4
                    "
                >
                    <p className="text-sm font-semibold text-[#ffd166]">
                        Active / Upcoming
                    </p>

                    <p className="mt-2 text-3xl font-bold text-[#fff3c4]">
                        {activeClassQuests.length}
                    </p>
                </div>

                <div
                    className="
                        rounded-2xl
                        border border-[#ffd166]/20
                        bg-[#080b14]
                        p-4
                    "
                >
                    <p className="text-sm font-semibold text-[#d8b4fe]">
                        Completed
                    </p>

                    <p className="mt-2 text-3xl font-bold text-[#fff3c4]">
                        {completedClassQuests.length}
                    </p>
                </div>
            </div>
                    <div className="mt-8">
                        <div className="mb-4 flex items-center justify-between gap-3">
                            <div>
                                <p className="text-xs uppercase tracking-[0.25em] text-[#d8b4fe]">
                                    Current Work
                                </p>

                                <h3 className="mt-1 font-display text-2xl text-[#ffd166]">
                                    Active / Upcoming Quests
                                </h3>
                            </div>

                            <span className="rounded-full border border-[#ffd166]/25 bg-[#080b14] px-3 py-1 text-xs text-[#fff3c4]">
                                {activeClassQuests.length}
                            </span>
                        </div>

                        {activeClassQuests.length === 0 ? (
                            <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-5">
                                <p className="text-sm text-[#b0b8d6]">
                                    No active quests for this class right now.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {activeClassQuests.map((quest) => {
                                    const questId =
                                        quest.quest_id ??
                                        quest.id;

                                    const questType =
                                        quest.quest_type ??
                                        quest.type ??
                                        "Quest";

                                    const questDate =
                                        quest.due_date ??
                                        quest.date_start ??
                                        quest.date;

                                    const questTime =
                                        quest.due_time ??
                                        quest.time;

                                    return (
                                        <div
                                            key={questId}
                                            className="
                                                rounded-[24px]
                                                border border-[#ffd166]/25
                                                bg-[#080b14]
                                                p-5
                                                shadow-[0_0_24px_rgba(255,209,102,0.06)]
                                            "
                                        >
                                            <div className="flex flex-wrap items-start justify-between gap-4">
                                                <div className="min-w-0 flex-1">
                                                    <div className="mb-2 flex flex-wrap items-center gap-2">
                                                        <span className="rounded-full border border-[#d8b4fe]/30 bg-[#2b1548]/50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#d8b4fe]">
                                                            {questType}
                                                        </span>

                                                        {quest.status && (
                                                            <span className="rounded-full border border-[#ffd166]/25 px-3 py-1 text-xs text-[#ffd166]">
                                                                {quest.status}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h4 className="text-xl font-semibold text-[#fff3c4]">
                                                        {quest.title ||
                                                            quest.name ||
                                                            "Untitled Quest"}
                                                    </h4>

                                                    {(quest.description ||
                                                        quest.instructions) && (
                                                        <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[#b0b8d6]">
                                                            {quest.description ||
                                                                quest.instructions}
                                                        </p>
                                                    )}
                                                {Array.isArray(quest.materials) &&
                                                    quest.materials.length > 0 && (
                                                        <div className="mt-4">
                                                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffd166]">
                                                                Materials
                                                            </p>

                                                            <ul className="mt-2 space-y-1">
                                                                {quest.materials.map(
                                                                    (material, index) => {
                                                                        const materialName =
                                                                            typeof material ===
                                                                            "string"
                                                                                ? material
                                                                                : material?.name ??
                                                                                material?.title ??
                                                                                material?.label ??
                                                                                "";

                                                                        if (!materialName) {
                                                                            return null;
                                                                        }

                                                                        return (
                                                                            <li
                                                                                key={`${questId}-material-${index}`}
                                                                                className="flex items-start gap-2 text-sm text-[#b0b8d6]"
                                                                            >
                                                                                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#d8b4fe]" />

                                                                                <span>
                                                                                    {materialName}
                                                                                </span>
                                                                            </li>
                                                                        );
                                                                    }
                                                                )}
                                                            </ul>
                                                        </div>
                                                    )}
                                {Array.isArray(quest.breakdown) &&
                                    quest.breakdown.length > 0 && (
                                        <div className="mt-5">
                                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffd166]">
                                                Quest Breakdown
                                            </p>

                                            <div className="mt-3 space-y-2">
                                                {quest.breakdown.map(
                                                    (step, index) => {
                                                        const stepTitle =
                                                            typeof step ===
                                                            "string"
                                                                ? step
                                                                : step?.title ??
                                                                step?.name ??
                                                                step?.label ??
                                                                `Step ${index + 1}`;

                                                        const stepDescription =
                                                            typeof step ===
                                                            "object"
                                                                ? step?.description ??
                                                                step?.instructions ??
                                                                ""
                                                                : "";

                                                        return (
                                                            <div
                                                                key={`${questId}-breakdown-${index}`}
                                                                className="rounded-2xl border border-[#ffd166]/15 bg-black/50 p-3"
                                                            >
                                                                <div className="flex items-start gap-3">
                                                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#d8b4fe]/30 bg-[#2b1548]/60 text-xs font-semibold text-[#d8b4fe]">
                                                                        {index + 1}
                                                                    </span>

                                                                    <div className="min-w-0">
                                                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                                                            {stepTitle}
                                                                        </p>

                                                                        {stepDescription && (
                                                                            <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-[#b0b8d6]">
                                                                                {stepDescription}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    )}                
                                                </div>

                                                {(questDate || questTime) && (
                                                    <div className="shrink-0 rounded-2xl border border-[#ffd166]/20 bg-black px-4 py-3 text-right">
                                                        <p className="text-[11px] uppercase tracking-[0.2em] text-[#d8b4fe]">
                                                            Due
                                                        </p>

                                                        {questDate && (
                                                            <p className="mt-1 text-sm font-semibold text-[#ffd166]">
                                                                {questDate}
                                                            </p>
                                                        )}

                                                        {questTime && (
                                                            <p className="mt-1 text-xs text-[#fff3c4]">
                                                                {questTime}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
        <div className="mt-10">
            <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#d8b4fe]">
                        Quest History
                    </p>

                    <h3 className="mt-1 font-display text-2xl text-[#ffd166]">
                        Completed Quests
                    </h3>
                </div>

                <span className="rounded-full border border-[#ffd166]/25 bg-[#080b14] px-3 py-1 text-xs text-[#fff3c4]">
                    {completedClassQuests.length}
                </span>
            </div>

            {completedClassQuests.length === 0 ? (
                <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-5">
                    <p className="text-sm text-[#b0b8d6]">
                        No completed quests for this class yet.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {completedClassQuests.map((quest) => {
                        const questId =
                            quest.quest_id ??
                            quest.id;

                        const questType =
                            quest.quest_type ??
                            quest.type ??
                            "Quest";

                        const questDate =
                            quest.due_date ??
                            quest.date_start ??
                            quest.date;

                        const questTime =
                            quest.due_time ??
                            quest.time;

                        return (
                            <div
                                key={questId}
                                className="
                                    rounded-[24px]
                                    border border-[#ffd166]/20
                                    bg-[#080b14]
                                    p-5
                                "
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="mb-2 flex flex-wrap items-center gap-2">
                                            <span className="rounded-full border border-[#d8b4fe]/30 bg-[#2b1548]/50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#d8b4fe]">
                                                {questType}
                                            </span>

                                            <span className="rounded-full border border-[#ffd166]/25 px-3 py-1 text-xs text-[#ffd166]">
                                                {quest.status || "Completed"}
                                            </span>
                                        </div>

                                        <h4 className="text-xl font-semibold text-[#fff3c4]">
                                            {quest.title ||
                                                quest.name ||
                                                "Untitled Quest"}
                                        </h4>

                                        {(quest.description ||
                                            quest.instructions) && (
                                            <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[#b0b8d6]">
                                                {quest.description ||
                                                    quest.instructions}
                                            </p>
                                        )}

                                        {Array.isArray(
                                            quest.materials
                                        ) &&
                                            quest.materials.length >
                                                0 && (
                                                <div className="mt-4">
                                                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffd166]">
                                                        Materials
                                                    </p>

                                                    <ul className="mt-2 space-y-1">
                                                        {quest.materials.map(
                                                            (
                                                                material,
                                                                index
                                                            ) => {
                                                                const materialName =
                                                                    typeof material ===
                                                                    "string"
                                                                        ? material
                                                                        : material?.name ??
                                                                        material?.title ??
                                                                        material?.label ??
                                                                        "";

                                                                if (
                                                                    !materialName
                                                                ) {
                                                                    return null;
                                                                }

                                                                return (
                                                                    <li
                                                                        key={`${questId}-completed-material-${index}`}
                                                                        className="flex items-start gap-2 text-sm text-[#b0b8d6]"
                                                                    >
                                                                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#d8b4fe]" />

                                                                        <span>
                                                                            {
                                                                                materialName
                                                                            }
                                                                        </span>
                                                                    </li>
                                                                );
                                                            }
                                                        )}
                                                    </ul>
                                                </div>
                                            )}

                                    {Array.isArray(quest.breakdown) &&
                                        quest.breakdown.length > 0 && (
                                            <div className="mt-5">
                                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffd166]">
                                                    Quest Breakdown
                                                </p>

                                                <div className="mt-3 space-y-2">
                                                    {quest.breakdown.map(
                                                        (step, index) => {
                                                            const stepTitle =
                                                                typeof step ===
                                                                "string"
                                                                    ? step
                                                                    : step?.title ??
                                                                    step?.name ??
                                                                    step?.label ??
                                                                    `Step ${index + 1}`;

                                                            const stepDescription =
                                                                typeof step ===
                                                                "object"
                                                                    ? step?.description ??
                                                                    step?.instructions ??
                                                                    ""
                                                                    : "";

                                                            return (
                                                                <div
                                                                    key={`${questId}-completed-breakdown-${index}`}
                                                                    className="rounded-2xl border border-[#ffd166]/15 bg-black/50 p-3"
                                                                >
                                                                    <div className="flex items-start gap-3">
                                                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#d8b4fe]/30 bg-[#2b1548]/60 text-xs font-semibold text-[#d8b4fe]">
                                                                            {index + 1}
                                                                        </span>

                                                                        <div className="min-w-0">
                                                                            <p className="text-sm font-semibold text-[#fff3c4]">
                                                                                {stepTitle}
                                                                            </p>

                                                                            {stepDescription && (
                                                                                <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-[#b0b8d6]">
                                                                                    {stepDescription}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    {Array.isArray(quest.rubric) &&
                                        quest.rubric.length > 0 && (
                                            <div className="mt-5">
                                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffd166]">
                                                    Rubric / Requirements
                                                </p>

                                                <div className="mt-3 space-y-2">
                                                    {quest.rubric.map(
                                                        (item, index) => {
                                                            const rubricTitle =
                                                                typeof item === "string"
                                                                    ? item
                                                                    : item?.title ??
                                                                    item?.name ??
                                                                    item?.criterion ??
                                                                    item?.label ??
                                                                    `Requirement ${index + 1}`;

                                                            const rubricDescription =
                                                                typeof item === "object"
                                                                    ? item?.description ??
                                                                    item?.details ??
                                                                    item?.instructions ??
                                                                    ""
                                                                    : "";

                                                            return (
                                                                <div
                                                                    key={`${questId}-rubric-${index}`}
                                                                    className="rounded-2xl border border-[#d8b4fe]/20 bg-[#2b1548]/20 p-3"
                                                                >
                                                                    <p className="text-sm font-semibold text-[#fff3c4]">
                                                                        {rubricTitle}
                                                                    </p>

                                                                    {rubricDescription && (
                                                                        <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-[#b0b8d6]">
                                                                            {rubricDescription}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    {Array.isArray(quest.rubric) &&
                                        quest.rubric.length > 0 && (
                                            <div className="mt-5">
                                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffd166]">
                                                    Rubric / Requirements
                                                </p>

                                                <div className="mt-3 space-y-2">
                                                    {quest.rubric.map(
                                                        (item, index) => {
                                                            const rubricTitle =
                                                                typeof item === "string"
                                                                    ? item
                                                                    : item?.title ??
                                                                    item?.name ??
                                                                    item?.criterion ??
                                                                    item?.label ??
                                                                    `Requirement ${index + 1}`;

                                                            const rubricDescription =
                                                                typeof item === "object"
                                                                    ? item?.description ??
                                                                    item?.details ??
                                                                    item?.instructions ??
                                                                    ""
                                                                    : "";

                                                            return (
                                                                <div
                                                                    key={`${questId}-completed-rubric-${index}`}
                                                                    className="rounded-2xl border border-[#d8b4fe]/20 bg-[#2b1548]/20 p-3"
                                                                >
                                                                    <p className="text-sm font-semibold text-[#fff3c4]">
                                                                        {rubricTitle}
                                                                    </p>

                                                                    {rubricDescription && (
                                                                        <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-[#b0b8d6]">
                                                                            {rubricDescription}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                            {(questDate ||
                                questTime) && (
                                <div className="shrink-0 rounded-2xl border border-[#ffd166]/20 bg-black px-4 py-3 text-right">
                                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#d8b4fe]">
                                        Due
                                    </p>

                                    {questDate && (
                                        <p className="mt-1 text-sm font-semibold text-[#ffd166]">
                                            {questDate}
                                        </p>
                                    )}

                                    {questTime && (
                                        <p className="mt-1 text-xs text-[#fff3c4]">
                                            {questTime}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    )}
</div>
        </section>
    );
}
        if (activePanel === "calendar") {
            return (
                <section className={shadowCalendarShell}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                                Read-Only Schedule
                            </p>

                            <h2 className="mt-1 font-display text-3xl text-[#ffd166]">
                                Class Calendar
                            </h2>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {["day", "week", "month"].map(
                                (view) => (
                                    <button
                                        key={view}
                                        type="button"
                                        onClick={() =>
                                            setCalendarView(
                                                view
                                            )
                                        }
                                        className={compactClassroomButtonClass(
                                            calendarView ===
                                                view
                                        )}
                                    >
                                        {view
                                            .charAt(0)
                                            .toUpperCase() +
                                            view.slice(1)}
                                    </button>
                                )
                            )}
                        </div>
                    </div>

                    <div className={`${shadowCalendarToolbar} mt-5 flex flex-wrap items-center justify-between gap-3`}>
                        <button
                            type="button"
                            onClick={() => {
                                const current =
                                    parseLocalDate(
                                        calendarSelectedDate
                                    );

                                if (
                                    calendarView ===
                                    "month"
                                ) {
                                    const previousMonth =
                                        new Date(
                                            calendarMonthDate.getFullYear(),
                                            calendarMonthDate.getMonth() -
                                                1,
                                            1
                                        );

                                    setCalendarMonthDate(
                                        previousMonth
                                    );

                                    setCalendarSelectedDate(
                                        formatLocalDate(
                                            previousMonth
                                        )
                                    );

                                    return;
                                }

                                current.setDate(
                                    current.getDate() -
                                        (calendarView ===
                                        "week"
                                            ? 7
                                            : 1)
                                );

                                setCalendarSelectedDate(
                                    formatLocalDate(
                                        current
                                    )
                                );
                            }}
                            className={purpleActionBtn}
                        >
                            Previous
                        </button>

                        <div className="text-center">
                            <p className="font-display text-2xl text-[#ffd166]">
                                {calendarView ===
                                "month"
                                    ? calendarMonthDate.toLocaleString(
                                        "default",
                                        {
                                            month: "long",
                                            year: "numeric",
                                        }
                                    )
                                    : calendarSelectedDate}
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                const current =
                                    parseLocalDate(
                                        calendarSelectedDate
                                    );

                                if (
                                    calendarView ===
                                    "month"
                                ) {
                                    const nextMonth =
                                        new Date(
                                            calendarMonthDate.getFullYear(),
                                            calendarMonthDate.getMonth() +
                                                1,
                                            1
                                        );

                                    setCalendarMonthDate(
                                        nextMonth
                                    );

                                    setCalendarSelectedDate(
                                        formatLocalDate(
                                            nextMonth
                                        )
                                    );

                                    return;
                                }

                                current.setDate(
                                    current.getDate() +
                                        (calendarView ===
                                        "week"
                                            ? 7
                                            : 1)
                                );

                                setCalendarSelectedDate(
                                    formatLocalDate(
                                        current
                                    )
                                );
                            }}
                            className={purpleActionBtn}
                        >
                            Next
                        </button>
                    </div>

                    {calendarView === "day" && (
                        <div className="mt-6 space-y-3">
                            {getCalendarEventsForDate(
                                calendarSelectedDate
                            ).length > 0 ? (
                                getCalendarEventsForDate(
                                    calendarSelectedDate
                                ).map((event) => (
                                    <div
                                        key={
                                            event.id ||
                                            event.quest_id
                                        }
                                        className="rounded-2xl border p-4" style={classroomCalendarStyle(event, selectedSubject?.color)} role="button" tabIndex={0} onClick={() => setSelectedChildQuestItem(event)} onKeyDown={keyEvent => { if (["Enter", " "].includes(keyEvent.key)) { keyEvent.preventDefault(); setSelectedChildQuestItem(event); } }}
                                    >
                                        <p className="font-display text-xl text-[#fff3c4]">
                                            {event.title ||
                                                event.name ||
                                                "Untitled"}
                                        </p>

                                        {(getEventStartTime(
                                            event
                                        ) ||
                                            getEventEndTime(
                                                event
                                            )) && (
                                            <p className="mt-1 text-sm text-[#ffd166]">
                                                {formatCalendarTime(
                                                    getEventStartTime(
                                                        event
                                                    )
                                                )}

                                                {getEventEndTime(
                                                    event
                                                )
                                                    ? ` - ${formatCalendarTime(
                                                        getEventEndTime(
                                                            event
                                                        )
                                                    )}`
                                                    : ""}
                                            </p>
                                        )}

                                        {event.description && (
                                            <p className="mt-2 text-sm text-[#b0b8d6]">
                                                {
                                                    event.description
                                                }
                                            </p>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-[#b0b8d6]">
                                    Nothing is scheduled for this day.
                                </p>
                            )}
                        </div>
                    )}

                    {calendarView === "week" && (
                        <div className="mt-6 grid gap-3 md:grid-cols-7">
                            {calendarWeekDates.map(
                                (dateString) => {
                                    const dayEvents =
                                        getCalendarEventsForDate(
                                            dateString
                                        );

                                    return (
                                        <button
                                            key={
                                                dateString
                                            }
                                            type="button"
                                            onClick={() => {
                                                setCalendarSelectedDate(
                                                    dateString
                                                );

                                                setCalendarView(
                                                    "day"
                                                );
                                            }}
                                            className={`${shadowCalendarCell} min-h-[180px]`}
                                        >
                                            <p className="text-xs uppercase tracking-[0.14em] text-[#d8b4fe]">
                                                {parseLocalDate(
                                                    dateString
                                                ).toLocaleDateString(
                                                    "default",
                                                    {
                                                        weekday:
                                                            "short",
                                                    }
                                                )}
                                            </p>

                                            <p className="mt-1 font-display text-lg text-[#ffd166]">
                                                {parseLocalDate(
                                                    dateString
                                                ).getDate()}
                                            </p>

                                            <div className="mt-3 space-y-2">
                                                {dayEvents
                                                    .slice(
                                                        0,
                                                        4
                                                    )
                                                    .map(
                                                        (
                                                            event
                                                        ) => (
                                                            <div
                                                                key={
                                                                    event.id ||
                                                                    event.quest_id
                                                                }
                                                                className="rounded-xl border p-2" style={classroomCalendarStyle(event, selectedSubject?.color)} role="button" tabIndex={0} onClick={() => setSelectedChildQuestItem(event)} onKeyDown={keyEvent => { if (["Enter", " "].includes(keyEvent.key)) { keyEvent.preventDefault(); setSelectedChildQuestItem(event); } }}
                                                            >
                                                                <p className="truncate text-xs font-semibold text-[#fff3c4]">
                                                                    {event.title ||
                                                                        event.name ||
                                                                        "Untitled"}
                                                                </p>

                                                                {getEventStartTime(
                                                                    event
                                                                ) && (
                                                                    <p className="mt-1 text-[10px] text-[#ffd166]">
                                                                        {formatCalendarTime(
                                                                            getEventStartTime(
                                                                                event
                                                                            )
                                                                        )}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )
                                                    )}

                                                {dayEvents.length >
                                                    4 && (
                                                    <p className="text-[10px] text-[#b0b8d6]">
                                                        +
                                                        {dayEvents.length -
                                                            4}{" "}
                                                        more
                                                    </p>
                                                )}
                                            </div>
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    )}

                    {calendarView === "month" && (
                        <div className="mt-6">
                            <div className="grid grid-cols-7 gap-2 text-center text-xs uppercase tracking-[0.12em] text-[#d8b4fe]">
                                {[
                                    "Sun",
                                    "Mon",
                                    "Tue",
                                    "Wed",
                                    "Thu",
                                    "Fri",
                                    "Sat",
                                ].map((day) => (
                                    <div
                                        key={day}
                                        className="py-2"
                                    >
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className={`${shadowCalendarGrid} grid-cols-7`}>
                                {calendarMonthDates.map(
                                    ({
                                        dateString,
                                        inCurrentMonth,
                                    }) => {
                                        const dayEvents =
                                            getCalendarEventsForDate(
                                                dateString
                                            );

                                        return (
                                            <button
                                                key={
                                                    dateString
                                                }
                                                type="button"
                                                onClick={() => {
                                                    setCalendarSelectedDate(
                                                        dateString
                                                    );

                                                    setCalendarView(
                                                        "day"
                                                    );
                                                }}
                                                className={`min-h-[115px] ${inCurrentMonth ? shadowCalendarCell : shadowCalendarCellMuted}`}
                                            >
                                                <p className="text-sm font-semibold text-[#ffd166]">
                                                    {parseLocalDate(
                                                        dateString
                                                    ).getDate()}
                                                </p>

                                                <div className="mt-2 space-y-1">
                                                    {dayEvents
                                                        .slice(
                                                            0,
                                                            3
                                                        )
                                                        .map(
                                                            (
                                                                event
                                                            ) => (
                                                                <p
                                                                    key={
                                                                        event.id ||
                                                                        event.quest_id
                                                                    }
                                                                    className="truncate rounded-lg border px-2 py-1 text-[10px] text-[#fff3c4]" style={classroomCalendarStyle(event, selectedSubject?.color)} role="button" tabIndex={0} onClick={() => setSelectedChildQuestItem(event)} onKeyDown={keyEvent => { if (["Enter", " "].includes(keyEvent.key)) { keyEvent.preventDefault(); setSelectedChildQuestItem(event); } }}
                                                                >
                                                                    {event.title ||
                                                                        event.name ||
                                                                        "Untitled"}
                                                                </p>
                                                            )
                                                        )}

                                                    {dayEvents.length >
                                                        3 && (
                                                        <p className="text-[10px] text-[#b0b8d6]">
                                                            +
                                                            {dayEvents.length -
                                                                3}
                                                        </p>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    }
                                )}
                            </div>
                        </div>
                    )}
                </section>
            );
        }
    if (activePanel === "archivum") {
        return (
            <section className="rounded-[32px] border border-[#ffd166]/40 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.10)]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                            Archivum
                        </p>

                        <h2 className="mt-1 font-display text-3xl text-[#ffd166]">
                            Curriculum & Resources
                        </h2>
                    </div>

                    {openArchivumFolderId && (
                        <button
                            type="button"
                            onClick={() => {
                                const currentFolder =
                                    selectedArchivumFolder;

                                setOpenArchivumFolderId(
                                    currentFolder?.parent_folder_id ||
                                        null
                                );
                            }}
                            className={purpleActionBtn}
                        >
                            <ArrowLeft size={16} />
                            Back One Folder
                        </button>
                    )}
                </div>

                {selectedArchivumFolder && (
                    <div className="mt-6 rounded-[28px] border border-[#ffd166]/25 bg-[#080b14] p-5">
                        <h3 className="font-display text-2xl text-[#ffd166]">
                            {selectedArchivumFolder.name ||
                                "Folder"}
                        </h3>

                        {selectedArchivumFolder.description && (
                            <p className="mt-2 text-sm text-[#b0b8d6]">
                                {
                                    selectedArchivumFolder.description
                                }
                            </p>
                        )}

                        {(selectedArchivumFolder.sticky_notes ||
                            []).length > 0 && (
                            <div className="mt-5 space-y-3">
                                {(selectedArchivumFolder.sticky_notes ||
                                    [])
                                    .slice()
                                    .sort(
                                        (a, b) =>
                                            Number(
                                                a.sort_order || 0
                                            ) -
                                            Number(
                                                b.sort_order || 0
                                            )
                                    )
                                    .map((note) => (
                                        <div
                                            key={
                                                note.note_id ||
                                                note.id
                                            }
                                            className="rounded-2xl border border-[#ffd166]/20 bg-black p-4"
                                        >
                                            <p className="font-semibold text-[#fff3c4]">
                                                {note.title ||
                                                    "Instruction"}
                                            </p>

                                            {note.content && (
                                                <p className="mt-2 text-sm leading-6 text-[#b0b8d6]">
                                                    {
                                                        note.content
                                                    }
                                                </p>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        )}

                        {(selectedArchivumFolder.resources ||
                            []).length > 0 && (
                            <div className="mt-6">
                                <p className="text-xs uppercase tracking-[0.16em] text-[#ffd166]">
                                    Resources
                                </p>

                                <div className="mt-3 grid gap-3 md:grid-cols-2">
                                    {(
                                        selectedArchivumFolder.resources ||
                                        []
                                    ).map((resource) => (
                                        <button
                                            key={
                                                resource.resource_id ||
                                                resource.id
                                            }
                                            type="button"
                                            onClick={() => {
                                                if (resource.url) {
                                                    window.open(
                                                        resource.url,
                                                        "_blank",
                                                        "noopener,noreferrer"
                                                    );

                                                    return;
                                                }

                                                if (
                                                    resource.resource_id
                                                ) {
                                                    window.open(
                                                        `${api.defaults.baseURL}/children/${childId}/classes/${subjectId}/archivum/resources/${resource.resource_id}`,
                                                        "_blank",
                                                        "noopener,noreferrer"
                                                    );
                                                }
                                            }}
                                            className="rounded-2xl border border-[#7c3aed]/45 bg-[#10091c] p-4 text-left transition hover:border-[#ffd166]/50"
                                        >
                                            <p className="font-semibold text-[#fff3c4]">
                                                {resource.title ||
                                                    "Resource"}
                                            </p>

                                            <p className="mt-1 text-xs uppercase tracking-[0.12em] text-[#c4a7ff]">
                                                {resource.resource_type ||
                                                    "resource"}
                                            </p>

                                            {resource.description && (
                                                <p className="mt-2 text-sm text-[#b0b8d6]">
                                                    {
                                                        resource.description
                                                    }
                                                </p>
                                            )}

                                            {resource.file_name && (
                                                <p className="mt-2 text-xs text-[#7f88a8]">
                                                    {
                                                        resource.file_name
                                                    }
                                                </p>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-6">
                    <p className="text-xs uppercase tracking-[0.16em] text-[#ffd166]">
                        Folders
                    </p>

                    {visibleArchivumFolders.length > 0 ? (
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                            {visibleArchivumFolders.map(
                                (folder) => (
                                    <button
                                        key={
                                            folder.id ||
                                            folder.folder_id
                                        }
                                        type="button"
                                        onClick={() =>
                                            setOpenArchivumFolderId(
                                                folder.id ||
                                                    folder.folder_id
                                            )
                                        }
                                        className="rounded-[24px] border border-[#ffd166]/30 bg-[#080b14] px-5 py-4 text-left transition hover:border-[#ffd166]/70"
                                    >
                                        <p className="font-display text-xl text-[#ffd166]">
                                            {folder.name ||
                                                "Folder"}
                                        </p>

                                        {folder.description && (
                                            <p className="mt-2 text-xs text-[#8f99b8]">
                                                {
                                                    folder.description
                                                }
                                            </p>
                                        )}

                                        <p className="mt-2 text-xs text-[#b0b8d6]">
                                            {
                                                (
                                                    classArchivum.folders ||
                                                    []
                                                ).filter(
                                                    (
                                                        childFolder
                                                    ) =>
                                                        String(
                                                            childFolder.parent_folder_id ||
                                                                ""
                                                        ) ===
                                                        String(
                                                            folder.id ||
                                                                folder.folder_id
                                                        )
                                                ).length
                                            }{" "}
                                            folders,{" "}
                                            {
                                                (
                                                    folder.resources ||
                                                    []
                                                ).length
                                            }{" "}
                                            resources
                                        </p>
                                    </button>
                                )
                            )}
                        </div>
                    ) : (
                        <p className="mt-3 text-sm text-[#b0b8d6]">
                            No folders are available here yet.
                        </p>
                    )}
                </div>
            </section>
        );
    }
        if (activePanel === "bulletin") return null;
        if (activePanel === "messages") return <HomeroomMessages key={childId} role="child" children={child ? [child] : []} />;

        return renderTodayPanel();
    };
const renderHomePanel = () => {
        return renderTodayPanel();
    };
    if (classroomLoading) return <div className="flex min-h-screen items-center justify-center bg-[#05070d] text-[#ffd166]">Opening classroom…</div>;
    if (classroomError) return <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#05070d] text-white"><p>{classroomError}</p><button className={purpleActionBtn} onClick={() => navigate("/child/login")}>Sign in again</button></div>;
    if (!child) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#05070d] text-white">
                Child classroom not found.
            </div>
        );
    }

    if (
        subjectId &&
        !selectedSubject
    ) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#05070d] text-white">
                Class not found.
            </div>
        );
    }

    return (
        <div
            className="
                min-h-screen
                text-white
                bg-[#05070d]
                bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.10),transparent_35%)]
            "
        >
<header className="relative z-10 border-b border-[#ffd166]/20 bg-black/60 backdrop-blur-sm">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <button
            type="button"
            onClick={() => {
                if (activePanel !== "lessonStage") {
                    setActivePanel("lessonStage");
                    return;
                }

                navigate(
                    `/child/home/${childId}`
                );
            }}
            className={purpleActionBtn}
        >
            <ArrowLeft size={16} />
            Back
        </button>

        <h1 className="flex items-center gap-2 font-display text-2xl text-[#ffd166]">
            <BookOpen size={22} />

            {selectedSubject?.name ||
                `${childName}'s Classroom`}
        </h1>

        <button
            type="button"
            onClick={() =>
                navigate(
                    `/child/break-room/${childId}`
                )
            }
            className={purpleActionBtn}
        >
            <Clock size={16} />
            Break Room
        </button>
    </div>
</header>

<main
    className="
        relative z-10
        w-full
        max-w-none
        px-4
        md:px-6
        xl:px-8
        py-8
        space-y-6
    "
>
    {renderHomeNavigation()}

    {renderHomePanel()}
</main>

            {lessonFlowModalSession && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/85 p-4">
                    <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[32px] border border-[#ffd166]/50 bg-black shadow-[0_0_40px_rgba(255,209,102,0.22)]">
                        <header className="flex items-center justify-between gap-4 border-b border-[#ffd166]/20 p-5">
                            <div>
                                <p className="text-xs uppercase tracking-[0.2em] text-[#d8b4fe]">
                                    Full Lesson Flow
                                </p>

                                <h3 className="mt-1 font-display text-3xl text-[#ffd166]">
                                    {lessonFlowModalSession
                                        .subject
                                        ?.name ||
                                        "Class Session"}
                                </h3>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setLessonFlowModalSession(
                                        null
                                    )
                                }
                                className={
                                    purpleActionBtn
                                }
                            >
                                <X size={16} />
                                Close
                            </button>
                        </header>

                        <div className="flex-1 space-y-3 overflow-y-auto p-5">
                            {getSessionSteps(
                                lessonFlowModalSession
                            ).map(
                                (
                                    step,
                                    index
                                ) => {
                                    const isActive =
                                        String(
                                            step.step_id
                                        ) ===
                                        String(
                                            lessonFlowModalSession
                                                .liveState
                                                ?.active_step_id
                                        );

                                    const stepColor =
                                        lessonFlowModalSession
                                            .subject
                                            ?.color ||
                                        "#ffd166";

                                    return (
                                        <div
                                            key={
                                                step.step_id ||
                                                index
                                            }
                                            className="rounded-2xl border bg-[#080b14] p-4"
                                            style={{
                                                borderColor:
                                                    isActive
                                                        ? stepColor
                                                        : "rgba(255,209,102,0.22)",
                                                boxShadow:
                                                    isActive
                                                        ? `0 0 18px ${stepColor}44`
                                                        : "none",
                                            }}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="text-xs uppercase tracking-[0.15em] text-[#d8b4fe]">
                                                        Section{" "}
                                                        {index +
                                                            1}
                                                    </p>

                                                    <h4
                                                        className="mt-1 font-display text-xl"
                                                        style={{
                                                            color: isActive
                                                                ? stepColor
                                                                : "#fff3c4",
                                                        }}
                                                    >
                                                        {step.title ||
                                                            "Untitled Section"}
                                                    </h4>
                                                </div>

                                                {isActive && (
                                                    <span
                                                        className="rounded-full border px-3 py-1 text-xs font-bold"
                                                        style={{
                                                            borderColor:
                                                                stepColor,
                                                            color: stepColor,
                                                        }}
                                                    >
                                                        Current
                                                    </span>
                                                )}
                                            </div>

                                            {step.description && (
                                                <p className="mt-3 text-sm text-[#b0b8d6]">
                                                    {
                                                        step.description
                                                    }
                                                </p>
                                            )}

                                            {Number(
                                                step.planned_minutes
                                            ) > 0 && (
                                                <p className="mt-3 text-xs text-[#ffd166]">
                                                    Planned:{" "}
                                                    {
                                                        step.planned_minutes
                                                    }{" "}
                                                    minutes
                                                </p>
                                            )}
                                        {selectedChildQuestItem && (
                                            <div
                                                className="
                                                    fixed
                                                    inset-0
                                                    z-[99999]
                                                    flex
                                                    items-center
                                                    justify-center
                                                    bg-black/90
                                                    px-4
                                                    backdrop-blur-sm
                                                "
                                                onMouseDown={(event) => {
                                                    if (
                                                        event.target ===
                                                        event.currentTarget
                                                    ) {
                                                        setSelectedChildQuestItem(
                                                            null
                                                        );
                                                    }
                                                }}
                                            >
                                                <div
                                                    className="
                                                        max-h-[90vh]
                                                        w-full
                                                        max-w-2xl
                                                        overflow-y-auto
                                                        rounded-[32px]
                                                        border
                                                        border-[#ffd166]/45
                                                        bg-[#05070d]
                                                        p-6
                                                        shadow-[0_0_45px_rgba(255,209,102,0.20)]
                                                    "
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d8b4fe]">
                                                                Quest Details
                                                            </p>

                                                            <h2 className="mt-2 font-display text-3xl text-[#ffd166]">
                                                                {selectedChildQuestItem.title ||
                                                                    selectedChildQuestItem.name ||
                                                                    "Untitled Quest"}
                                                            </h2>

                                                            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                                                <span className="rounded-full border border-[#d8b4fe]/30 bg-[#2b1548]/50 px-3 py-1 text-[#d8b4fe]">
                                                                    {selectedChildQuestItem.type ||
                                                                        selectedChildQuestItem.quest_type ||
                                                                        "Quest"}
                                                                </span>

                                                                {selectedChildQuestItem.status && (
                                                                    <span className="rounded-full border border-[#ffd166]/25 px-3 py-1 text-[#ffd166]">
                                                                        {selectedChildQuestItem.status}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setSelectedChildQuestItem(
                                                                    null
                                                                )
                                                            }
                                                            className="
                                                                flex
                                                                h-9
                                                                w-9
                                                                shrink-0
                                                                items-center
                                                                justify-center
                                                                rounded-xl
                                                                border
                                                                border-[#ffd166]/35
                                                                bg-black
                                                                text-lg
                                                                text-[#ffd166]
                                                                transition
                                                                hover:border-[#c084fc]/70
                                                                hover:text-[#e9d5ff]
                                                            "
                                                            aria-label="Close quest details"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>

                                                    <LessonCalendarDetails event={selectedChildQuestItem} />
                                                    {(selectedChildQuestItem.description ||
                                                        selectedChildQuestItem.instructions) && (
                                                        <div className="mt-6 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#ffd166]">
                                                                Instructions
                                                            </p>

                                                            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#b0b8d6]">
                                                                {selectedChildQuestItem.instructions ||
                                                                    selectedChildQuestItem.description}
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                        <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                                            <p className="text-[10px] uppercase tracking-[0.18em] text-[#d8b4fe]">
                                                                Due
                                                            </p>

                                                            <p className="mt-1 text-sm font-semibold text-[#fff3c4]">
                                                                {getChildQuestDueDate(
                                                                    selectedChildQuestItem
                                                                ) || "No due date"}
                                                            </p>

                                                            {selectedChildQuestItem.due_time && (
                                                                <p className="mt-1 text-xs text-[#ffd166]">
                                                                    {selectedChildQuestItem.due_time}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                                            <p className="text-[10px] uppercase tracking-[0.18em] text-[#d8b4fe]">
                                                                Status
                                                            </p>

                                                            <p className="mt-1 text-sm font-semibold text-[#fff3c4]">
                                                                {selectedChildQuestItem.status ||
                                                                    "Open"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {Array.isArray(
                                                        selectedChildQuestItem.materials
                                                    ) &&
                                                        selectedChildQuestItem.materials.length >
                                                            0 && (
                                                            <div className="mt-4 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#ffd166]">
                                                                    Materials
                                                                </p>

                                                                <ul className="mt-2 space-y-1">
                                                                    {selectedChildQuestItem.materials.map(
                                                                        (
                                                                            material,
                                                                            index
                                                                        ) => {
                                                                            const materialName =
                                                                                typeof material ===
                                                                                "string"
                                                                                    ? material
                                                                                    : material?.name ??
                                                                                    material?.title ??
                                                                                    material?.label ??
                                                                                    "";

                                                                            if (!materialName) {
                                                                                return null;
                                                                            }

                                                                            return (
                                                                                <li
                                                                                    key={`selected-material-${index}`}
                                                                                    className="flex items-start gap-2 text-sm text-[#b0b8d6]"
                                                                                >
                                                                                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#d8b4fe]" />

                                                                                    <span>
                                                                                        {
                                                                                            materialName
                                                                                        }
                                                                                    </span>
                                                                                </li>
                                                                            );
                                                                        }
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        )}

                                                    {Array.isArray(
                                                        selectedChildQuestItem.breakdown
                                                    ) &&
                                                        selectedChildQuestItem.breakdown.length >
                                                            0 && (
                                                            <div className="mt-4 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#ffd166]">
                                                                    Quest Breakdown
                                                                </p>

                                                                <div className="mt-3 space-y-2">
                                                                    {selectedChildQuestItem.breakdown.map(
                                                                        (
                                                                            step,
                                                                            index
                                                                        ) => (
                                                                            <div
                                                                                key={`selected-breakdown-${index}`}
                                                                                className="rounded-xl border border-[#ffd166]/15 bg-black/40 p-3"
                                                                            >
                                                                                <p className="text-sm font-semibold text-[#fff3c4]">
                                                                                    {typeof step ===
                                                                                    "string"
                                                                                        ? step
                                                                                        : step?.title ??
                                                                                        step?.name ??
                                                                                        `Step ${
                                                                                            index +
                                                                                            1
                                                                                        }`}
                                                                                </p>
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                    {Array.isArray(
                                                        selectedChildQuestItem.rubric
                                                    ) &&
                                                        selectedChildQuestItem.rubric.length >
                                                            0 && (
                                                            <div className="mt-4 rounded-2xl border border-[#c084fc]/20 bg-[#160d26]/35 p-4">
                                                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#e9d5ff]">
                                                                    Rubric / Requirements
                                                                </p>

                                                                <div className="mt-3 space-y-2">
                                                                    {selectedChildQuestItem.rubric.map(
                                                                        (
                                                                            item,
                                                                            index
                                                                        ) => (
                                                                            <div
                                                                                key={`selected-rubric-${index}`}
                                                                                className="rounded-xl border border-[#c084fc]/20 bg-black/40 p-3"
                                                                            >
                                                                                <p className="text-sm font-semibold text-[#fff3c4]">
                                                                                    {typeof item ===
                                                                                    "string"
                                                                                        ? item
                                                                                        : item?.title ??
                                                                                        item?.name ??
                                                                                        item?.criterion ??
                                                                                        `Requirement ${
                                                                                            index +
                                                                                            1
                                                                                        }`}
                                                                                </p>
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        )}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
